// Garden 3.1 — a true-perspective free-orbit pixel diorama that grows while your agent works.
// Everything procedural: seeded hill bowl, server-owned pond, plants. The SCENE keeps its own
// ambient clock now — a full 24h day every 5 minutes, weather drifting every 2 — while the
// ECONOMY stays on the server's word. Single canvas, 5fps stepped animation.
import { h } from 'preact';
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import htm from 'htm';
import {
  hashString, mulberry32, spriteFor, paintSprite, animateSprite, petSeedFor,
  drawPlant as drawMiniPlant, ACC_TINT, PALETTES, SPRITE_W, SPRITE_H,
} from './pet.js';
import { brandGrid } from './brand.js';
import { agentLabel } from './fun.js';
// the fish art moved into closet.js beside its aquarium dex — the import only
// ever flows garden → closet, so no cycle
import { Closet, FishCanvas } from './closet.js';

const html = htm.bind(h);

// ---- shared world functions (server mirrors these exactly — see server/src/api.js) ----
// these stay the ECONOMY's truth (bad-weather chip, meteor odds); the renderer no longer
// reads them — the scene lives on the ambient clock below instead.

export const utcDay = () => new Date().toISOString().slice(0, 10);

export function weatherFor(day) {
  const r = hashString(`vb:weather:${day}`) % 100;
  if (r < 62) return 'sunny';
  if (r < 77) return 'cloudy';
  if (r < 85) return 'rain';
  if (r < 89) return 'storm';
  if (r < 94) return 'fog';
  if (r < 98) return 'snow';
  return 'meteor';
}

export function timeStage(hour = new Date().getHours()) {
  if (hour >= 5 && hour < 8) return 'dawn';
  if (hour >= 8 && hour < 17) return 'day';
  if (hour >= 17 && hour < 20) return 'dusk';
  return 'night';
}

// ---- scene constants ----

const W = 272;
const H = 208;
const GRID = 10; // the plot is GRID x GRID tiles — the server is the source of truth for size & pond
const CX = (GRID - 1) / 2; // 4.5 — the plot pivot, in tile units
const DIST = 15; // camera orbit radius, tile units
const FOCAL = 230; // perspective focal length, px
const PITCH_MIN = 0.07; // nearly level with the grass — the postcard shot
const PITCH_MAX = 1.15;
const MEADOW_R = 28; // the ground plane ENDS here (tile units) — beyond is ridge haze, never grass
const WATER_Z = -0.25; // the pond surface sits sunken below the meadow — a real recess
const DEF_YAW = -0.5;
const DEF_PITCH = 0.42;
const SUN_AZ = 2.1; // the sun/moon live at one fixed world azimuth — tilt flat and face it to watch the sunset
const DIST_MIN = 9; // wheel dolly clamps, tile units
const DIST_MAX = 24;
const HGT_MIN = -1; // middle-drag eye-height offset clamps
const HGT_MAX = 5;

// a real orbit camera: world X/Y is the ground plane in tile units, Z points up.
// pure math, no shared state — every GardenCanvas keeps its own yaw/pitch.
// exported so the offline probe can round-trip project/unproject against the shipped code.
// vw/vh parametrize the viewport: windowed stays the classic 272x208, fullscreen hands
// in its native internal resolution and a wider canvas genuinely widens the FOV
export function camera(yaw, pitch, dist = DIST, hgt = 0, vw = W, vh = H) {
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const ex = CX + dist * cp * cy;
  const ey = CX + dist * cp * sy;
  const ez = Math.max(0.4, dist * sp + hgt); // the eye never sinks under the meadow
  // basis: f looks at the plot center, r is screen-right, u is screen-up (no roll)
  const f = [-cp * cy, -cp * sy, -sp];
  const r = [-sy, cy, 0];
  const u = [-sp * cy, -sp * sy, cp];
  // the principal point slides up as the camera climbs so the plot stays framed at
  // every pitch — 150/208 of the height, exactly the classic 150 on the 208 canvas
  const oy = vh * (150 / 208) - 42 * (pitch - DEF_PITCH);
  const project = (x, y, z = 0) => {
    const vx = x - ex;
    const vy = y - ey;
    const vz = z - ez;
    const d = vx * f[0] + vy * f[1] + vz * f[2];
    if (d < 0.5) return null; // behind the lens
    return [vw / 2 + (FOCAL * (vx * r[0] + vy * r[1])) / d, oy - (FOCAL * (vx * u[0] + vy * u[1] + vz * u[2])) / d, d];
  };
  // directions at infinity: sun, moon, stars
  const projectDir = (dx, dy, dz) => {
    const d = dx * f[0] + dy * f[1] + dz * f[2];
    if (d < 0.05) return null;
    return [vw / 2 + (FOCAL * (dx * r[0] + dy * r[1])) / d, oy - (FOCAL * (dx * u[0] + dy * u[1] + dz * u[2])) / d];
  };
  // pointer ray ∩ a horizontal plane (z=0 ground by default; the sunken water plane
  // when fishing) — the exact inverse of project for points on that plane
  const unproject = (mx, my, z = 0) => {
    const px = (mx - vw / 2) / FOCAL;
    const py = (oy - my) / FOCAL;
    const dx = f[0] + px * r[0] + py * u[0];
    const dy = f[1] + px * r[1] + py * u[1];
    const dz = f[2] + py * u[2]; // r has no z component
    if (dz >= -1e-9) return null; // pointing above the horizon
    const t = (z - ez) / dz;
    return [ex + t * dx, ey + t * dy];
  };
  return { project, projectDir, unproject, horizonY: oy - FOCAL * Math.tan(pitch) };
}

// grab-the-world drag: mutate cam.yaw/cam.pitch so the ground point P grabbed at
// pointerdown stays pinned under the cursor (mx,my). yaw is analytic — camera() is
// equivariant under rotation about the plot's vertical axis, so FOR A GIVEN PITCH the
// yaw that puts P at the cursor's azimuth is one closed-form delta (the azimuth gap
// between P and the cursor's current ground hit, both measured around CX,CX). pitch
// rides a 1-D solve on top: the projected error is NOT monotonic in pitch (its slope
// crosses zero for near ground — the eye climbs while the principal point slides
// down), so no Newton/secant; scan the legal range with yaw re-aligned per candidate,
// then ternary-refine the winner. a whisper of proximity bias (6px per rad) keeps the
// lens still in the flat band where pitch barely moves the point at all.
// exported so the offline math probe can drive the shipped code.
export function grabSolve(cam, P, mx, my, vw = W, vh = H) {
  const pAz = Math.atan2(P[1] - CX, P[0] - CX);
  const pR = Math.hypot(P[0] - CX, P[1] - CX);
  const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));
  const solveAt = (p) => {
    const g = camera(cam.yaw, p, cam.dist, cam.hgt, vw, vh).unproject(mx, my);
    if (!g) return null; // this pitch puts the cursor over the sky
    let yaw = cam.yaw;
    // near the orbit axis azimuth is ill-conditioned (and physically yaw-free): hold yaw
    if (pR > 0.4 && Math.hypot(g[0] - CX, g[1] - CX) > 0.4)
      yaw = cam.yaw + wrap(pAz - Math.atan2(g[1] - CX, g[0] - CX));
    const pr = camera(yaw, p, cam.dist, cam.hgt, vw, vh).project(P[0], P[1], 0);
    if (!pr) return null;
    return { yaw, err: Math.hypot(pr[0] - mx, pr[1] - my) };
  };
  let bestP = cam.pitch;
  let best = solveAt(bestP);
  if (!best) return; // cursor over the sky right now — hold the last solved frame
  const N = 32;
  const span = PITCH_MAX - PITCH_MIN;
  const biased = (s, p) => s.err + Math.abs(p - cam.pitch) * 6;
  // coarse scan, then ternary-refine EVERY local minimum — the true basin can be a
  // narrow deep V whose coarse samples read higher than some broad shallow bowl
  const es = [];
  for (let i = 0; i <= N; i++) es.push(solveAt(PITCH_MIN + (span * i) / N));
  const eAt = (i) => (es[i] ? es[i].err : Infinity);
  for (let i = 0; i <= N; i++) {
    if (!es[i]) continue;
    if (eAt(i) > eAt(i - 1) || eAt(i) > eAt(i + 1)) continue; // not a local min
    let lo = clampPitch(PITCH_MIN + (span * (i - 1)) / N);
    let hi = clampPitch(PITCH_MIN + (span * (i + 1)) / N);
    let cand = es[i];
    let candP = PITCH_MIN + (span * i) / N;
    for (let it = 0; it < 24; it++) {
      const m1 = lo + (hi - lo) / 3;
      const m2 = hi - (hi - lo) / 3;
      const s1 = solveAt(m1);
      const s2 = solveAt(m2);
      // a null probe = that pitch throws the cursor over the sky; treat it as
      // infinitely bad and keep shrinking — the basin can hug the sky boundary
      if ((s1 ? s1.err : Infinity) < (s2 ? s2.err : Infinity)) {
        hi = m2;
        if (s1 && s1.err < cand.err) {
          cand = s1;
          candP = m1;
        }
      } else {
        lo = m1;
        if (s2 && s2.err < cand.err) {
          cand = s2;
          candP = m2;
        }
      }
    }
    if (biased(cand, candP) < biased(best, bestP)) {
      best = cand;
      bestP = candP;
    }
  }
  cam.pitch = clampPitch(bestP);
  cam.yaw = best.yaw;
}

const spriteScale = (d) => Math.max(0.6, Math.min(1.6, DIST / d));
const clampPitch = (p) => Math.max(PITCH_MIN, Math.min(PITCH_MAX, p));

// ---- the ambient clock: the scene lives a full 24h day every 5 minutes and swaps
// weather every 2 — pure client theatre (performance.now; no Date, no server field).
// The ECONOMY keeps reading server data (water.bonus, meteor fishing odds, butterfly
// clocks) — only the visuals breathe on this loop.

const DAY_S = 300;
const WEATHER_S = 120;
// WEIGHTED weather bag: sunny x3, cloudy x2, the dramatic moods x1 each — 10 slots,
// reshuffled per epoch, so rain stops dominating but every mood still lands in a sitting
const WEATHER_BAG = ['sunny', 'sunny', 'sunny', 'cloudy', 'cloudy', 'rain', 'storm', 'fog', 'snow', 'meteor'];
// stage bands as fractions of the loop — night gets ~35% (the fireflies and the
// cottage window need their moment); dawn and dusk stay short and golden
const STAGE_EDGE = [['dawn', 0], ['day', 0.1], ['dusk', 0.475], ['night', 0.65]];

export function ambientNow(nowS = performance.now() / 1000) {
  const dayFrac = (nowS % DAY_S) / DAY_S;
  let stage = 'night';
  for (const [name, at] of STAGE_EDGE) if (dayFrac >= at) stage = name;
  // weather draws from a seeded shuffled WEIGHTED bag, reshuffled each pass —
  // every mood shows up inside a ~20-minute sitting, no repeats until the bag empties
  const slot = Math.floor(nowS / WEATHER_S);
  const epoch = Math.floor(slot / WEATHER_BAG.length);
  const bag = WEATHER_BAG.slice();
  const rnd = mulberry32((0x5eed ^ Math.imul(epoch + 1, 0x9e3779b1)) >>> 0);
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  return { dayFrac, stage, weather: bag[slot % bag.length], slot };
}

// palettes crossfade around each stage boundary (~21s of the loop) so the light
// glides instead of snapping — returns [fromStage, toStage, blend 0..1]
export function stagePhase(dayFrac) {
  const B = 0.035;
  const bounds = [
    [0.1, 'dawn', 'day'],
    [0.475, 'day', 'dusk'],
    [0.65, 'dusk', 'night'],
    [1, 'night', 'dawn'], // the loop seam — night melts back into dawn
    [0, 'night', 'dawn'],
  ];
  for (const [p, a, b] of bounds) {
    const d = dayFrac - p;
    if (d >= -B && d < B) return [a, b, (d + B) / (2 * B)];
  }
  let cur = 'night';
  for (const [name, at] of STAGE_EDGE) if (dayFrac >= at) cur = name;
  return [cur, cur, 0];
}

// a continuous sun/moon arc across the ambient day: azimuth sweeps past SUN_AZ while
// the elevation rises and falls — the light visibly travels instead of teleporting
export function celestial(dayFrac) {
  const nightAt = 0.65;
  if (dayFrac >= nightAt) {
    const f = (dayFrac - nightAt) / (1 - nightAt); // the moon takes the night shift
    return { az: SUN_AZ + (f - 0.5) * 1.5, el: 0.12 + Math.sin(Math.PI * f) * 0.55 };
  }
  const f = dayFrac / nightAt;
  return { az: SUN_AZ + (f - 0.5) * 1.7, el: 0.03 + Math.sin(Math.PI * f) * 1.02 };
}

// four-stage light. each: sky bands (top->horizon), hill ring, meadow ground, tile checker,
// soil ring, water pair (near-tones — the pond ripples, it must never strobe), global tint.
// tints are [r,g,b,a] and dim is a number so stage boundaries can crossfade numerically
const STAGES = {
  dawn: {
    sky: ['#b8a8cc', '#d4bcc4', '#f6dfc8', '#f3cba6', '#eeb894', '#eaa987'],
    hillFar: '#c99a8c', hillNear: '#a97c74',
    ground: ['#96ab6e', '#8a9e62'],
    tileA: '#a8cf7e', tileB: '#98c06f',
    dirt: ['#8a6a4a', '#7b5d40'],
    water: ['#a9c9de', '#b2d1e4'],
    sun: '#ffd9a0', stars: false,
    tint: [255, 170, 185, 0.13], dim: 0,
  },
  day: {
    sky: ['#9ecfe9', '#aedaf0', '#bfe3f2', '#cdeaf6', '#dbf0fa', '#e7f6fc'],
    hillFar: '#9fc2ae', hillNear: '#7ea98c',
    ground: ['#82b96e', '#77ac63'],
    tileA: '#9fd98a', tileB: '#8ccc78',
    dirt: ['#8a6a4a', '#7b5d40'],
    water: ['#93c8e8', '#9dcfec'],
    sun: '#ffe9a8', stars: false,
    tint: [255, 255, 255, 0], dim: 0,
  },
  dusk: {
    sky: ['#655283', '#8c5f86', '#f0b58e', '#e5907e', '#cf7a8b', '#e8987f'],
    hillFar: '#9a7386', hillNear: '#7d5c72',
    ground: ['#719459', '#688750'],
    tileA: '#8bb46f', tileB: '#7ca261',
    dirt: ['#7c5e40', '#6d5238'],
    water: ['#c89aad', '#cfa2b4'],
    sun: '#ffb36b', stars: false,
    tint: [255, 150, 60, 0.16], dim: 0,
  },
  night: {
    // palette runs a notch brighter than it reads — the global blue tint pulls it down
    sky: ['#202440', '#2b3152', '#343b63', '#3f4773', '#4a527f', '#555d8a'],
    hillFar: '#454e73', hillNear: '#3a4263',
    ground: ['#4c6a4b', '#446044'],
    tileA: '#5e8a64', tileB: '#527a58',
    dirt: ['#584838', '#4c3e2e'],
    water: ['#54699a', '#5b70a0'],
    sun: '#e8e6d8', stars: true, // the moon
    tint: [70, 90, 180, 0.3], dim: 0.2,
  },
};

// the frame's light: a pure stage deep inside a band, a numeric crossfade near boundaries
function blendStage(a, b, t) {
  const A = STAGES[a];
  if (a === b || t <= 0) return A;
  const Bs = STAGES[b];
  if (t >= 1) return Bs;
  const L = (x, y) => x + (y - x) * t;
  return {
    sky: A.sky.map((c, i) => mix(c, Bs.sky[i], t)),
    hillFar: mix(A.hillFar, Bs.hillFar, t),
    hillNear: mix(A.hillNear, Bs.hillNear, t),
    ground: [mix(A.ground[0], Bs.ground[0], t), mix(A.ground[1], Bs.ground[1], t)],
    tileA: mix(A.tileA, Bs.tileA, t),
    tileB: mix(A.tileB, Bs.tileB, t),
    dirt: [mix(A.dirt[0], Bs.dirt[0], t), mix(A.dirt[1], Bs.dirt[1], t)],
    water: [mix(A.water[0], Bs.water[0], t), mix(A.water[1], Bs.water[1], t)],
    sun: mix(A.sun, Bs.sun, t),
    stars: (t < 0.5 ? A : Bs).stars,
    tint: A.tint.map((v, i) => L(v, Bs.tint[i])),
    dim: L(A.dim, Bs.dim),
  };
}

const WEATHER_LABEL = { sunny: 'sunny', cloudy: 'cloudy', rain: 'rainy', storm: 'stormy', fog: 'foggy', snow: 'snowy', meteor: 'meteor night' };

// stable display spots for plants that predate tiles (px null): seeded, collision-free
function placed(plants, pond) {
  const used = new Set(plants.filter((p) => p.px != null).map((p) => `${p.px},${p.py}`));
  return plants.map((p) => {
    if (p.px != null) return p;
    const rnd = mulberry32(p.seed >>> 0);
    for (let tries = 0; tries < 96; tries++) {
      const x = Math.floor(rnd() * GRID);
      const y = Math.floor(rnd() * GRID);
      const k = `${x},${y}`;
      if (!pond.has(k) && !used.has(k)) {
        used.add(k);
        return { ...p, px: x, py: y, unsaved: true };
      }
    }
    for (let x = 0; x < GRID; x++)
      for (let y = 0; y < GRID; y++) {
        const k = `${x},${y}`;
        if (!pond.has(k) && !used.has(k)) {
          used.add(k);
          return { ...p, px: x, py: y, unsaved: true };
        }
      }
    return { ...p, px: 0, py: 0, unsaved: true };
  });
}

// rejection-sample a dry tile, with a deterministic scan as the fallback —
// the pet must never spawn (or get retargeted) into the pond
function dryTile(pond) {
  for (let i = 0; i < 40; i++) {
    const x = Math.floor(Math.random() * GRID);
    const y = Math.floor(Math.random() * GRID);
    if (!pond.has(`${x},${y}`)) return [x, y];
  }
  for (let x = 0; x < GRID; x++)
    for (let y = 0; y < GRID; y++) if (!pond.has(`${x},${y}`)) return [x, y];
  return [0, 0];
}

// butterflies orbit the plot in WORLD space now — one formula feeds both the
// renderer and the pet's chase brain
const flutter = (b, t) => [
  CX + Math.sin(t * b.speed * 8 + b.phase) * b.r,
  CX + Math.cos(t * b.speed * 6 + b.phase * 1.7) * b.r,
  1.1 + Math.sin(t * 0.9 + b.phase) * 0.35,
];

// ---- pixel drawing helpers ----

function quad(ctx, pts, color) {
  ctx.fillStyle = color;
  ctx.beginPath();
  pts.forEach(([qx, qy], i) => (i ? ctx.lineTo(qx, qy) : ctx.moveTo(qx, qy)));
  ctx.closePath();
  ctx.fill();
}

// a ground tile as a projected quad — jagged edges at odd angles are the retro charm.
// z sinks the quad below the meadow: the pond surface lives at WATER_Z
function tileQuad(ctx, cam, x, y, color, z = 0) {
  const c = cam.project(x, y, z);
  if (!c) return;
  const pts = [];
  for (const [qx, qy] of [[x - 0.5, y - 0.5], [x + 0.5, y - 0.5], [x + 0.5, y + 0.5], [x - 0.5, y + 0.5]]) {
    const p = cam.project(qx, qy, z);
    if (!p) return;
    // bloat corners outward a hair so neighbours overlap instead of seaming
    const d = Math.hypot(p[0] - c[0], p[1] - c[1]) || 1;
    pts.push([p[0] + ((p[0] - c[0]) / d) * 0.6, p[1] + ((p[1] - c[1]) / d) * 0.6]);
  }
  quad(ctx, pts, color);
}

// the finite meadow: project the rim of the ground disc and fill it. when part of
// the rim swings behind the lens (low pitch), the visible arc closes far below the
// screen — the ground underfoot is always inside the disc, so the fill stays honest
function groundDisc(ctx, cam, rr, color) {
  const N = 48;
  const pts = [];
  let gap = -1;
  for (let i = 0; i < N; i++) {
    const az = (i / N) * Math.PI * 2;
    const p = cam.project(CX + Math.cos(az) * rr, CX + Math.sin(az) * rr, 0);
    pts.push(p);
    if (!p) gap = i;
  }
  ctx.fillStyle = color;
  ctx.beginPath();
  let first = true;
  for (let i = 0; i < N; i++) {
    const p = pts[gap < 0 ? i : (gap + 1 + i) % N];
    if (!p) continue;
    if (first) ctx.moveTo(p[0], p[1]);
    else ctx.lineTo(p[0], p[1]);
    first = false;
  }
  if (first) return;
  if (gap >= 0) {
    // close beneath our feet, far off-canvas on both sides
    ctx.lineTo(1e5, 1e5);
    ctx.lineTo(-1e5, 1e5);
  }
  ctx.closePath();
  ctx.fill();
}

// a thin quad hugging one tile edge (facing dx,dy) — the shore lip where grass meets water
function edgeStrip(ctx, cam, x, y, dx, dy, color) {
  const pts = [];
  for (const [ax, ay] of [
    [x + dx * 0.5 - dy * 0.5, y + dy * 0.5 - dx * 0.5],
    [x + dx * 0.5 + dy * 0.5, y + dy * 0.5 + dx * 0.5],
    [x + dx * 0.28 + dy * 0.5, y + dy * 0.28 + dx * 0.5],
    [x + dx * 0.28 - dy * 0.5, y + dy * 0.28 - dx * 0.5],
  ]) {
    const p = cam.project(ax, ay, 0);
    if (!p) return;
    pts.push([p[0], p[1]]);
  }
  quad(ctx, pts, color);
}

// lerp two #rrggbb hexes — the melt between plot and meadow tones
function mix(a, b, t) {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  let out = '#';
  for (const sh of [16, 8, 0]) {
    const v = Math.round(((pa >> sh) & 255) * (1 - t) + ((pb >> sh) & 255) * t);
    out += v.toString(16).padStart(2, '0');
  }
  return out;
}

// flat ground decals (dirt patches, stepping stones) — seeded jittered quads on z=0,
// projected per frame so they sit IN the meadow instead of floating on it
function decalQuad(ctx, cam, dc, stage) {
  const pts = [];
  for (let i = 0; i < 4; i++) {
    const [ux, uy] = [[-1, -1], [1, -1], [1, 1], [-1, 1]][i];
    const p = cam.project(dc.x + ux * dc.w * (0.6 + dc.j[i] * 0.5), dc.y + uy * dc.w * (0.6 + dc.j[3 - i] * 0.5), 0);
    if (!p) return;
    pts.push([p[0], p[1]]);
  }
  if (dc.stone) {
    quad(ctx, pts.map(([qx, qy]) => [qx, qy + 1]), '#6f6a7e');
    quad(ctx, pts, '#9a93a8');
  } else {
    quad(ctx, pts, stage.dirt[dc.tone]);
  }
}

// soft blob shadow under a billboard: one flat ellipse scaled with the sprite's perspective
function blobShadow(ctx, sx, sy, rw, rh, a = 0.14) {
  ctx.fillStyle = `rgba(0,0,0,${a})`;
  ctx.beginPath();
  ctx.ellipse(sx, sy, Math.max(1.5, rw), Math.max(1, rh), 0, 0, Math.PI * 2);
  ctx.fill();
}

// ---- the cottage: one hand-set pixel grid, world-anchored billboard (landmark-sized) ----

const HOUSE_GRID = [
  '...............CCC...',
  '.........RR....CCC...',
  '........RRRR...CCC...',
  '.......RRRRRR..CCC...',
  '......RRRRRRRR.CCC...',
  '.....RRRRRRRRRRCCC...',
  '....RRRRRRRRRRRRRR...',
  '...RRRRRRRRRRRRRRRR..',
  '..RRRRRRRRRRRRRRRRRR.',
  '.RRRRRRRRRRRRRRRRRRR.',
  'rrrrrrrrrrrrrrrrrrrrr',
  '.WWWWWWWWWWWWWWWWWWW.',
  '.WWGGGGWWWWWWWDDDWWW.',
  '.WWGGGGWWWWWWWDDDWWW.',
  '.WWGGGGWWWWWWWDDDWWW.',
  '.WWWWWWWWWWWWWDDDWWW.',
  '.WWWWWWWWWWWWWDDDWWW.',
  '.WWWWWWWWWWWWWDDDWWW.',
  '.wwwwwwwwwwwwwDDDwww.',
];
const HOUSE_COL = { C: '#8b8496', R: '#b3573b', r: '#8e4430', W: '#e6d3a7', w: '#c9b48c', D: '#7a5a3a' };

function drawHouse(ctx, sx, sy, d, night, w) {
  // true perspective with a WIDE clamp (~0.5x..2.2x of the default-dolly size):
  // wheel-in genuinely grows the cottage now, wheel-out shrinks it — like the plants.
  // cells round cumulatively so fractional sizes stay crisp with no seams
  const base = (FOCAL * 0.165) / DIST;
  const uf = Math.max(base * 0.5, Math.min(base * 2.2, (FOCAL * 0.165) / d));
  const gw = HOUSE_GRID[0].length;
  const gh = HOUSE_GRID.length;
  const x0 = Math.round(sx - (gw * uf) / 2);
  const y0 = Math.round(sy - gh * uf) + 1;
  const at = (i) => Math.round(i * uf);
  blobShadow(ctx, sx, Math.round(sy), (gw * uf) / 2 - uf, Math.max(2, Math.round(uf * 1.6)), 0.16);
  for (let y = 0; y < gh; y++)
    for (let x = 0; x < gw; x++) {
      const ch = HOUSE_GRID[y][x];
      if (ch === '.') continue;
      ctx.fillStyle = ch === 'G' ? (night ? '#ffd98a' : '#8fa3b8') : HOUSE_COL[ch];
      ctx.fillRect(x0 + at(x), y0 + at(y), at(x + 1) - at(x), at(y + 1) - at(y));
    }
  // remember the lit window so the post-tint pass can bloom it (grid cols 3-6, rows 12-14)
  w.houseWin = night
    ? { x: x0 + at(3), y: y0 + at(12), w: at(7) - at(3), h: at(15) - at(12), u: Math.max(1, Math.round(uf)) }
    : null;
}

// ---- plant sprites (billboards on tiles, 1 unit = 2px at scale 1) ----
// the whole chain re-drawn with 3 seeded variants per kind so gardens differ;
// coordinates below: x in units right of the anchor, y in units ABOVE the ground

// where THE tree hangs its golden fruit, in sprite units — shared by the
// renderer and the canvas hit-test so a tap lands exactly on the glow
export function fruitSpot(seed) {
  const r = mulberry32((seed ^ 0xf407) >>> 0);
  return [Math.floor(r() * 11) - 5, 20 + Math.floor(r() * 6)];
}

function drawPlant(ctx, p, cx, cy, frame, sc = 1, fruit = false) {
  const rnd = mulberry32(p.seed >>> 0);
  const pal = PALETTES[Math.floor(rnd() * PALETTES.length)];
  const v = Math.floor(rnd() * 3); // the variant die
  const U = 2 * sc;
  const R = (x, y, w, h, c) => {
    ctx.fillStyle = c;
    ctx.fillRect(cx + x * U, cy - y * U, w * U, h * U);
  };
  const sway = p.kind === 'seed' ? 0 : (frame + (p.seed % 4)) % 4 < 2 ? 0 : 1;
  const stem = '#1d9e75';
  const leaf = '#9fe1cb';
  const dark = '#177a5c';
  const bark = '#7a5a3a';
  const barkDk = '#5c4126';
  // soft blob shadow hugging the ground — the tree earns a landmark's pool
  blobShadow(ctx, cx, cy - 1 * sc, (p.kind === 'tree' ? 11 : 5.5) * sc, (p.kind === 'tree' ? 3 : 1.8) * sc);
  if (p.kind === 'seed') {
    // a soft soil mound with a hopeful tip; the tip leans with the variant
    R(-3, 1, 6, 1, '#5c4936');
    R(-2, 2, 4, 1, bark);
    R(-1, 3, 2, 1, '#8a6a48');
    R(v === 1 ? -1 : 0, 4, 1, 1, stem);
    if (v === 2) R(2.6, 1, 1.4, 1, '#9a93a8'); // a lucky pebble neighbour
    // water pips: how many drops it has drunk (the contract keeps these)
    for (let i = 0; i < 3; i++) R(-3 + i * 2.5, 8, 1.5, 1.5, i < p.water_count ? '#5aa9e6' : 'rgba(120,120,130,0.35)');
  } else if (p.kind === 'grass') {
    if (v === 0) {
      // the fountain: blades arcing out both ways
      R(-3.5, 4, 1, 4, leaf);
      R(-4.3, 5, 1, 1, leaf);
      R(-1.8, 6, 1, 6, stem);
      R(-1.8 - sway * 0.6, 7, 1, 1, stem);
      R(-0.2, 7.5, 1, 7.5, dark);
      R(-0.2 + sway * 0.6, 8.5, 1, 1, leaf);
      R(1.4, 5.5, 1, 5.5, stem);
      R(2.2, 6.5, 1, 1, stem);
      R(3, 3.5, 1, 3.5, leaf);
      R(3.8, 4.5, 1, 1, leaf);
    } else if (v === 1) {
      // windswept, one wheat-brown seed head nodding on the tallest blade
      R(-4, 3, 1, 3, dark);
      R(-2.5, 5, 1, 5, stem);
      R(-1, 7.5, 1, 7.5, dark);
      R(-1 + sway * 0.7, 8.6, 1.2, 1.2, bark);
      R(0.5, 6, 1, 6, stem);
      R(2, 4, 1, 4, leaf);
      R(2.8, 4.8, 1, 1, leaf);
    } else {
      // a round tuft — and a ladybug peeking out, antennae flicking
      R(-3, 5, 1, 5, dark);
      R(-1.5, 6.5, 1, 6.5, stem);
      R(0, 7 + sway * 0.5, 1, 7 + sway * 0.5, leaf);
      R(1.5, 5.5, 1, 5.5, stem);
      R(-4, 3.5, 1, 3.5, leaf);
      R(2.6, 2, 2, 2, '#d85a30');
      R(3.2, 1.6, 0.7, 0.7, '#232326');
      R(2.2, 2.4, 0.7, 0.7, '#232326');
      R(2 + (frame % 2 ? 0.4 : 0), 3.2, 0.5, 0.8, '#232326');
    }
  } else if (p.kind === 'flower') {
    const hx = sway * 0.5;
    if (v === 0) {
      // tulip: a proud two-tone cup on a leafed stem
      R(0, 8, 1, 8, stem);
      R(-2, 4, 2, 1, leaf);
      R(1, 5, 2, 1, leaf);
      R(-2 + hx, 11, 4, 3, pal[0]);
      R(-2 + hx, 12, 1, 1, pal[0]);
      R(1 + hx, 12, 1, 1, pal[0]);
      R(-1 + hx, 11, 2, 1, pal[1]);
    } else if (v === 1) {
      // daisy: a petal cross around a golden heart
      R(0, 9, 1, 9, stem);
      R(-2, 4, 2, 1, leaf);
      R(1.2, 5.5, 1.6, 1, leaf);
      R(-2.5 + hx, 11.5, 2, 2, pal[0]);
      R(1.5 + hx, 11.5, 2, 2, pal[0]);
      R(-0.5 + hx, 13.5, 2, 2, pal[0]);
      R(-0.5 + hx, 9.5, 2, 2, pal[0]);
      R(-0.5 + hx, 11.5, 2, 2, '#f5c542');
    } else {
      // bellflower: bells hanging off a curved stalk
      R(0, 7, 1, 7, stem);
      R(0.7, 9, 1, 2.2, stem);
      R(1.6, 10.2, 1.2, 1.4, stem);
      R(-2, 4, 2, 1, leaf);
      R(1.2 + hx, 9, 3, 2.2, pal[0]);
      R(1.2 + hx, 6.8, 3, 0.7, pal[1]);
      R(2.3 + hx, 6, 0.8, 0.8, '#f5c542');
      R(-1.4, 6.4, 1.2, 0.6, stem);
      R(-2.6, 6, 2.2, 1.6, pal[0]);
      R(-2.6, 4.4, 2.2, 0.6, pal[1]);
    }
  } else if (p.kind === 'sapling') {
    // a young tree finding its shape: real trunk, loose layered canopy, light sway
    const cano = v === 2 ? pal[0] : '#5cae57';
    const canoDk = v === 2 ? pal[1] : '#2f7d43';
    R(-0.75, 7, 1.5, 7, bark);
    R(0.2, 5, 0.55, 2, barkDk);
    if (v === 1) R(-2.2, 5, 1.6, 0.7, bark); // a low branch stub
    R(-3.5, 11, 7, 4, cano);
    R(-2.5, 13.5, 5, 2.5, cano);
    R(-1.5, 15.5 + sway * 0.5, 3, 2, cano);
    R(-2.5, 10, 2, 1.5, canoDk);
    R(1, 12, 1.5, 1.5, canoDk);
    R(-1.1, 8, 2.2, 1, canoDk); // the shaded underside
    R(-1 + (frame % 4 < 2 ? 0 : 1.2), 14, 1, 1, mix(cano, '#ffffff', 0.35)); // a leaf in the light
  } else {
    // THE TREE — the monument every garden works toward. ~2.5x the sapling,
    // wind-rounded layered canopy, bark with a knot, and (when ripe) the fruit
    const tinted = rnd() < 0.25;
    const cano = tinted ? pal[0] : '#5cae57';
    const canoDk = tinted ? pal[1] : '#2f7d43';
    const canoHi = mix(cano, '#fff8d8', 0.35);
    // trunk: root flare, tall taper, seeded bark streaks, one knot
    R(-3.5, 2, 7, 2, bark);
    R(-2.5, 16, 5, 16, bark);
    R(-1.5, 18, 3, 3, bark);
    R(-1.6, 14, 1, 10, barkDk);
    R(1.1, 11, 0.7, 7, barkDk);
    const ky = 5 + Math.floor(rnd() * 5);
    R(-0.6, ky, 1.6, 1.6, barkDk);
    R(-0.3, ky - 0.4, 0.8, 0.8, '#3a2a18');
    // limbs shouldering up into the canopy
    R(-5.5, 18.5, 3.5, 1.4, bark);
    R(2.5, 17.5, 3.5, 1.4, bark);
    // canopy: hand-set profile, each band nudged by the seed for a living outline
    const bands = [
      [16, 9], [18, 10.5], [20, 11.5], [22, 12], [24, 12], [26, 11.5],
      [28, 11], [30, 10], [32, 8.5], [34, 7], [36, 5.5], [38, 4],
    ];
    for (const [by, hw] of bands) {
      const jx = (rnd() - 0.5) * 2;
      R(-hw + jx, by + 2, hw * 2, 2.2, cano);
    }
    R(-2, 41 + sway * 0.5, 4, 2, cano); // the crown tuft, breathing
    // dappled shading: seeded dark leaf-clumps, a dark belly, lit crown
    R(-9, 17.6, 18, 1.6, canoDk);
    for (let i = 0; i < 9; i++) {
      const dx = (rnd() - 0.5) * 18;
      const dy = 19 + rnd() * 17;
      R(dx, dy, 1.4 + rnd(), 1.3, rnd() < 0.6 ? canoDk : mix(cano, canoDk, 0.5));
    }
    R(-3, 39.5, 5, 1.2, canoHi);
    R(-6.5, 34, 3, 1, canoHi);
    R(3.5, 36, 2.5, 1, canoHi);
    R(frame % 4 < 2 ? -4 : 3, 32, 1, 1, '#ffffff'); // the classic leaf glint
    if (rnd() < 0.6) {
      R(5.5, 24, 1.4, 1.4, pal[1]); // seeded blossoms tucked in the green
      R(-6.5, 28, 1.4, 1.4, pal[1]);
    }
    if (fruit) {
      // the golden promise: a glowing fruit low in the canopy, pulsing gently
      const [fx, fy] = fruitSpot(p.seed);
      ctx.fillStyle = frame % 4 < 2 ? 'rgba(245,197,66,0.32)' : 'rgba(245,197,66,0.2)';
      ctx.fillRect(cx + (fx - 2.6) * U, cy - (fy + 2.6) * U, 5.2 * U, 5.2 * U);
      R(fx - 0.4, fy + 2.6, 0.8, 1.2, bark); // its stem
      R(fx - 1.5, fy + 1.5, 3, 3, '#f5c542');
      R(fx - 1.5, fy + 1.5, 1.2, 1.2, '#fff3c4'); // shine
      if (frame % 4 < 2) {
        R(fx - 3.2, fy, 0.8, 0.8, '#fff3c4');
        R(fx + 2.6, fy + 3.2, 0.8, 0.8, '#fff3c4');
      } else {
        R(fx + 0.2, fy + 4.6, 0.8, 0.8, '#fff3c4');
      }
    }
  }
}

function drawBuddy(ctx, seed, sx, sy, frame, napping, meta, cell = 2) {
  const sprite = spriteFor(seed, { stage: meta?.stage ?? 'adult', wins: meta?.wins ?? 0, equip: meta?.equip });
  const bob = napping ? 0 : frame % 2;
  paintSprite(ctx, sprite, sx - (SPRITE_W * cell) / 2, sy - SPRITE_H * cell + 2 + bob, cell, {
    napping,
    blink: frame % 9 === 8,
    frame,
  });
  if (napping && frame % 6 < 3) {
    ctx.fillStyle = '#cfd4e6';
    ctx.fillRect(sx + 5 * cell, sy - 13 * cell, 2, 2);
    ctx.fillRect(sx + 6.5 * cell, sy - 15 * cell, 2, 2);
  }
}

// seeded meadow scatter outside the plot: grass tufts, pebbles, tiny flowers
function drawDetail(ctx, dt, sx, sy, sc, stage) {
  const x = Math.round(sx);
  const y = Math.round(sy);
  const u = sc >= 1.2 ? 2 : 1;
  if (dt.t === 0) {
    ctx.fillStyle = stage.ground[1];
    ctx.fillRect(x - 2 * u, y - 3 * u, u, 3 * u);
    ctx.fillRect(x, y - 4 * u, u, 4 * u);
    ctx.fillRect(x + 2 * u, y - 2 * u, u, 2 * u);
  } else if (dt.t === 1) {
    ctx.fillStyle = '#9a93a8';
    ctx.fillRect(x - u, y - u, 3 * u, u);
    ctx.fillStyle = '#6f6a7e';
    ctx.fillRect(x - u, y, 3 * u, u);
  } else if (dt.t === 2) {
    ctx.fillStyle = '#1d9e75';
    ctx.fillRect(x, y - 2 * u, u, 2 * u);
    ctx.fillStyle = dt.hue[0];
    ctx.fillRect(x - u, y - 4 * u, 3 * u, 2 * u);
    ctx.fillStyle = dt.hue[1];
    ctx.fillRect(x, y - 3 * u, u, u);
  } else if (dt.t === 3) {
    // a broader clump, blades leaning apart with one lit blade
    ctx.fillStyle = stage.ground[1];
    ctx.fillRect(x - 3 * u, y - 3 * u, u, 3 * u);
    ctx.fillRect(x - u, y - 4 * u, u, 4 * u);
    ctx.fillRect(x + u, y - 4 * u, u, 4 * u);
    ctx.fillRect(x + 3 * u, y - 2 * u, u, 2 * u);
    ctx.fillStyle = stage.tileA;
    ctx.fillRect(x, y - 3 * u, u, 3 * u);
  } else if (dt.t === 5) {
    // shore reeds: three leaning stalks, seed-brown heads — pond-bank furniture
    ctx.fillStyle = '#2f7d4f';
    ctx.fillRect(x - u, y - 5 * u, u, 5 * u);
    ctx.fillRect(x + u, y - 4 * u, u, 4 * u);
    ctx.fillStyle = '#1d9e75';
    ctx.fillRect(x, y - 6 * u, u, 6 * u);
    ctx.fillStyle = '#7a5a3a';
    ctx.fillRect(x, y - 8 * u, u, 2 * u);
    ctx.fillRect(x - u, y - 6 * u, u, u);
  } else {
    // a pair of daisies
    ctx.fillStyle = '#e8ecf2';
    ctx.fillRect(x - 2 * u, y - 3 * u, 2 * u, 2 * u);
    ctx.fillRect(x + u, y - 4 * u, 2 * u, 2 * u);
    ctx.fillStyle = '#f5c542';
    ctx.fillRect(x - 2 * u, y - 3 * u, u, u);
    ctx.fillRect(x + u, y - 4 * u, u, u);
  }
}

// ---- the scene component ----

export function GardenCanvas({ data, visits = [], self = false, onAct, onToast, mode, onPlanted, onMovedTo, onSelect, selected, petMeta, onPat, onFish, onHarvest, camRef, fs = false, cameraDrift = 0, clockOverride = null }) {
  const petMetaRef = useRef(petMeta);
  petMetaRef.current = petMeta;
  // demo mode: cameraDrift (rad/s) auto-orbits the yaw and locks out pointer camera
  // control; clockOverride {dayFrac} pins the ambient clock (stage from dayFrac, clear sky)
  const driftRef = useRef(cameraDrift);
  driftRef.current = cameraDrift;
  const clockRef = useRef(clockOverride);
  clockRef.current = clockOverride;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const canvasRef = useRef(null);
  // camera state lives HERE, per mounted garden — two gardens never share a lens
  const world = useRef({
    frame: 0, pet: null, drops: [], flakes: [], meteors: [], hearts: [], ripples: [], wslot: -1,
    drag: null, camDrag: null, hgtDrag: null, gid: null, hover: null, px: 1, vw: W, vh: H,
    cam: { yaw: DEF_YAW, pitch: DEF_PITCH, dist: DIST, hgt: 0 },
  });
  const stateRef = useRef({});
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // hand the panel a reset handle for the ⟲ view button
  useEffect(() => {
    if (!camRef) return;
    camRef.current = {
      reset() {
        world.current.cam.yaw = DEF_YAW;
        world.current.cam.pitch = DEF_PITCH;
        world.current.cam.dist = DIST;
        world.current.cam.hgt = 0;
      },
    };
    return () => { camRef.current = null; };
  }, [camRef]);

  // rebuild fixtures when data changes
  useEffect(() => {
    if (!data) return;
    const ownerSeed = petSeedFor(data.user.username);
    // the pond is SERVER data — the client never computes its own mask
    const pond = new Set(data.pond ?? []);
    const plants = placed(data.plants ?? [], pond);
    // pond geometry: centroid + rim radius feed the depth/sky gradients; shore edges
    // get sand/mud lips; a few reeds and lily pads dress the bank
    const pondTiles = [...pond].map((k) => k.split(',').map(Number));
    let pcx = CX, pcy = CX, pondR = 1;
    if (pondTiles.length) {
      pcx = pondTiles.reduce((a, [x]) => a + x, 0) / pondTiles.length;
      pcy = pondTiles.reduce((a, [, y]) => a + y, 0) / pondTiles.length;
      pondR = Math.max(0.8, ...pondTiles.map(([x, y]) => Math.hypot(x - pcx, y - pcy))) + 0.5;
    }
    const srnd = mulberry32(ownerSeed ^ 0x5ea1);
    const sand = new Map(); // land tile key -> [[dx,dy] toward the water, ...]
    const reeds = [];
    for (const [wx, wy] of pondTiles)
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const lx = wx + dx;
        const ly = wy + dy;
        if (lx < 0 || lx >= GRID || ly < 0 || ly >= GRID || pond.has(`${lx},${ly}`)) continue;
        const lk = `${lx},${ly}`;
        if (!sand.has(lk)) sand.set(lk, []);
        sand.get(lk).push([-dx, -dy]);
        if (reeds.length < 3 && srnd() < 0.3)
          reeds.push({ x: wx + dx * 0.62, y: wy + dy * 0.62, t: 5, hue: PALETTES[0] });
      }
    const lilies = pondTiles.length
      ? Array.from({ length: 2 }, () => {
          const [lx, ly] = pondTiles[Math.floor(srnd() * pondTiles.length)];
          return { x: lx + (pcx - lx) * 0.3 + (srnd() - 0.5) * 0.3, y: ly + (pcy - ly) * 0.3 + (srnd() - 0.5) * 0.3 };
        })
      : [];
    // butterflies live one day, then fly off (leaving a sip on a seed, server-side)
    const bNow = Date.now() / 1000;
    const butterflies = (visits ?? [])
      .filter((v) => v.action === 'butterfly' && bNow - v.created_at < 24 * 3600)
      .slice(0, 5)
      .map((v, i) => ({
        hue: PALETTES[hashString(v.visitor) % PALETTES.length],
        phase: (hashString(v.visitor) % 100) / 16,
        speed: 0.11 + (i % 3) * 0.03,
        r: 4.6 + (i % 3) * 0.7,
        fading: bNow - v.created_at > 21 * 3600, // their last hours flicker
      }));
    // panorama fixtures — seeded once per owner, anchored in world azimuths.
    // four ridge rings close the valley: near ring saturated and detailed, mid ring rolling,
    // far ring dense, and a MOUNTAIN wall behind them all — big silhouettes tall enough to
    // keep their heads up even when the lens looks down from the pitch ceiling
    const hrnd = mulberry32(ownerSeed ^ 0x411f);
    const hills = [];
    const ringSpec = [
      { n: 7 + Math.floor(hrnd() * 3), r0: 17, rv: 3.5, wd0: 12, wdv: 9 },
      { n: 12 + Math.floor(hrnd() * 3), r0: 25, rv: 4, wd0: 17, wdv: 10 },
      { n: 16 + Math.floor(hrnd() * 4), r0: 34, rv: 5, wd0: 26, wdv: 10 },
      { n: 10 + Math.floor(hrnd() * 3), r0: 47, rv: 7, wd0: 42, wdv: 16 },
    ];
    ringSpec.forEach((spec, ring) => {
      for (let i = 0; i < spec.n; i++) {
        const az = ((i + hrnd() * 0.5) / spec.n) * Math.PI * 2;
        const rr = spec.r0 + hrnd() * spec.rv;
        // strong size variety: a few towering peaks, many mid mounds, some low rollers —
        // except the outer rings, whose job is sealing the horizon (no low rollers back there)
        const roll = hrnd();
        const h =
          ring === 3
            ? roll < 0.35 ? 17 + hrnd() * 5 : 12 + hrnd() * 4
            : ring === 2
              ? roll < 0.2 ? 10 + hrnd() * 3 : 6.5 + hrnd() * 2.5
              : roll < 0.18 ? 7.5 + hrnd() * 2.5 : roll < 0.72 ? 4.4 + hrnd() * 2.2 : 2.8 + hrnd() * 1.2;
        hills.push({
          x: CX + Math.cos(az) * rr,
          y: CX + Math.sin(az) * rr,
          h, wd: spec.wd0 + hrnd() * spec.wdv, ring,
          snow: ring === 3 ? h >= 15 : h >= 9,
          facet: hrnd() < 0.5 ? -1 : 1,
          jseed: (ownerSeed ^ Math.imul(hills.length + 1, 0x9e3779b1)) >>> 0,
          trees: ring === 0 ? Array.from({ length: 3 + Math.floor(hrnd() * 3) }, () => [hrnd() * 1.4 - 0.7, hrnd() * 0.22]) : null,
        });
      }
    });
    const drnd = mulberry32(ownerSeed ^ 0xdeca);
    const detail = [];
    for (let i = 0; i < 500 && detail.length < 90; i++) {
      const az = drnd() * Math.PI * 2;
      const rr = 5.5 + drnd() * 11; // stays inside the near ridge, so no tuft pokes through a hill
      const dx = CX + Math.cos(az) * rr;
      const dy = CX + Math.sin(az) * rr;
      const t = Math.floor(drnd() * 5);
      const hue = PALETTES[Math.floor(drnd() * PALETTES.length)];
      // keep the random scatter just off the plot — the boundary tufts below own that seam
      if (dx > -1.6 && dx < GRID + 0.6 && dy > -1.6 && dy < GRID + 0.6) continue;
      detail.push({ x: dx, y: dy, t, hue });
    }
    // boundary tufts: anchored just OUTSIDE the tile line so their blades stand half-in
    const ernd = mulberry32(ownerSeed ^ 0xed6e);
    for (let i = 0; i < 16; i++) {
      const along = -0.4 + ernd() * (GRID - 0.2);
      const out = 0.5 + 0.12 + ernd() * 0.45;
      const side = i % 4;
      const [dx, dy] =
        side === 0 ? [along, -out] : side === 1 ? [along, GRID - 1 + out] : side === 2 ? [-out, along] : [GRID - 1 + out, along];
      detail.push({ x: dx, y: dy, t: ernd() < 0.7 ? 0 : 3, hue: PALETTES[0] });
    }
    // meadow mottling: flat two-tone world blobs — they swing with the camera, so the
    // grass reads textured in every direction at every yaw
    const mrnd = mulberry32(ownerSeed ^ 0x0d17);
    const mottle = [];
    for (let i = 0; i < 600 && mottle.length < 150; i++) {
      const az = mrnd() * Math.PI * 2;
      const rr = 5.2 + mrnd() * (MEADOW_R - 9); // never past the meadow's rim
      const mx = CX + Math.cos(az) * rr;
      const my = CX + Math.sin(az) * rr;
      if (mx > -1.4 && mx < GRID + 0.4 && my > -1.4 && my < GRID + 0.4) continue;
      mottle.push({ x: mx, y: my, w: 0.35 + mrnd() * 0.75, tone: mrnd() < 0.55 });
    }
    // the cottage: a seeded corner 2-3 tiles outside the plot, plus a stone path home
    const crnd = mulberry32(ownerSeed ^ 0xc077);
    const hc = Math.floor(crnd() * 4);
    const house = {
      x: (hc & 1 ? GRID + 1.6 : -2.6) + (crnd() - 0.5) * 0.8,
      y: (hc & 2 ? GRID + 1.6 : -2.6) + (crnd() - 0.5) * 0.8,
    };
    // sparse dirt patches + stepping stones stand in for the old solid soil ring
    const decals = [];
    for (let i = 0; i < 9; i++) {
      const along = -0.3 + crnd() * (GRID - 0.4);
      const out = 0.5 + 0.25 + crnd() * 0.5;
      const side = i % 4;
      const [dx, dy] =
        side === 0 ? [along, -out] : side === 1 ? [along, GRID - 1 + out] : side === 2 ? [-out, along] : [GRID - 1 + out, along];
      decals.push({ x: dx, y: dy, w: 0.28 + crnd() * 0.3, stone: crnd() < 0.3, tone: i % 2, j: [crnd(), crnd(), crnd(), crnd()] });
    }
    // 4 stepping stones from the house door toward the nearest plot edge
    const gx = Math.max(-0.5, Math.min(GRID - 0.5, house.x));
    const gy = Math.max(-0.5, Math.min(GRID - 0.5, house.y));
    for (let i = 0; i < 4; i++) {
      const f = 0.2 + i * 0.2;
      decals.push({
        x: house.x + (gx - house.x) * f + (crnd() - 0.5) * 0.25,
        y: house.y + (gy - house.y) * f + (crnd() - 0.5) * 0.25,
        w: 0.16 + crnd() * 0.08, stone: true, tone: 0, j: [crnd(), crnd(), crnd(), crnd()],
      });
    }
    // reeds ride the billboard list with the other ground details
    detail.push(...reeds);
    stateRef.current = {
      ownerSeed, pond, plants, butterflies, hills, detail, mottle, house, decals,
      pondTiles, pondC: [pcx, pcy], pondR, sand, lilies,
      username: data.user.username,
      fruitReady: !!data.fruit_ready, // only the self payload carries it — friends see no glow
      // note: data.weather stays server truth for the ECONOMY — the scene ignores it
    };
    const w = world.current;
    if (!w.pet) {
      const [sx0, sy0] = dryTile(pond);
      w.pet = { x: sx0, y: sy0, state: 'idle', timer: 8, tx: sx0, ty: sy0 };
    }
    // rain/snow particle pools are owned by the ambient weather slot in draw()
  }, [data, visits]);

  // pet brain — one thought per frame
  const tickPet = (s) => {
    const pet = world.current.pet;
    if (!pet) return;
    const free = (x, y) => x >= 0 && x < GRID && y >= 0 && y < GRID && !s.pond.has(`${x},${y}`);
    if (pet.state === 'pat') {
      if (--pet.timer <= 0) pet.state = 'idle';
      return;
    }
    if (pet.state === 'idle' || pet.state === 'sit') {
      if (--pet.timer > 0) return;
      const roll = Math.random();
      const trees = s.plants.filter((p) => p.kind === 'tree' || p.kind === 'sapling');
      if (roll < 0.42) {
        let tx, ty, guard = 0;
        do {
          tx = Math.floor(Math.random() * GRID);
          ty = Math.floor(Math.random() * GRID);
        } while (!free(tx, ty) && ++guard < 20);
        Object.assign(pet, { state: 'walk', tx, ty });
      } else if (roll < 0.55 && s.butterflies.length) {
        Object.assign(pet, { state: 'chase', timer: 34 });
      } else if (roll < 0.68 && trees.length) {
        const t = trees[Math.floor(Math.random() * trees.length)];
        const spots = [[t.px + 1, t.py], [t.px - 1, t.py], [t.px, t.py + 1]].filter(([x, y]) => free(x, y));
        if (spots.length) Object.assign(pet, { state: 'walk', tx: spots[0][0], ty: spots[0][1], thenNap: true });
        else pet.timer = 10;
      } else if (roll < 0.78) {
        Object.assign(pet, { state: 'hop', timer: 8 });
      } else if (roll < 0.86) {
        Object.assign(pet, { state: 'wiggle', timer: 8 });
      } else if (roll < 0.94) {
        // amble to the shore and watch the water think
        const shore = [];
        for (const k of s.pond) {
          const [wx, wy] = k.split(',').map(Number);
          for (const [ax, ay] of [[1, 0], [-1, 0], [0, 1], [0, -1]])
            if (free(wx + ax, wy + ay)) shore.push([wx + ax, wy + ay]);
        }
        if (shore.length) {
          const [gx, gy] = shore[Math.floor(Math.random() * shore.length)];
          Object.assign(pet, { state: 'walk', tx: gx, ty: gy, thenSit: true });
        } else Object.assign(pet, { state: 'sit', timer: 12 });
      } else {
        Object.assign(pet, { state: 'sit', timer: 10 + Math.random() * 20 });
      }
      return;
    }
    if (pet.state === 'hop' || pet.state === 'wiggle') {
      if (--pet.timer <= 0) Object.assign(pet, { state: 'idle', timer: 6 + Math.random() * 10 });
      return;
    }
    if (pet.state === 'nap') {
      if (--pet.timer <= 0) Object.assign(pet, { state: 'idle', timer: 5 });
      return;
    }
    if (pet.state === 'chase') {
      const t = world.current.frame * 0.12;
      const b = s.butterflies[0];
      if (!b || --pet.timer <= 0) return Object.assign(pet, { state: 'idle', timer: 6 });
      const [bx, by] = flutter(b, t);
      pet.tx = Math.max(0, Math.min(GRID - 1, bx));
      pet.ty = Math.max(0, Math.min(GRID - 1, by));
    }
    // walk / chase movement, axis by axis like a proper little sprite —
    // and never a paw in the pond: blocked legs swap axes, and when BOTH
    // axes block, pick a fresh dry target instead of freezing forever
    const step = 0.14;
    const wet = (fx, fy) => s.pond.has(`${Math.round(fx)},${Math.round(fy)}`);
    const dx = pet.tx - pet.x;
    const dy = pet.ty - pet.y;
    if (Math.abs(dx) > step || Math.abs(dy) > step) {
      const tryX = () => {
        if (Math.abs(dx) <= step) return false;
        const nx = pet.x + Math.sign(dx) * step;
        if (wet(nx, pet.y)) return false;
        pet.x = nx;
        return true;
      };
      const tryY = () => {
        if (Math.abs(dy) <= step) return false;
        const ny = pet.y + Math.sign(dy) * step;
        if (wet(pet.x, ny)) return false;
        pet.y = ny;
        return true;
      };
      if (!tryX() && !tryY()) {
        if (pet.state === 'chase') Object.assign(pet, { state: 'idle', timer: 4, thenNap: false, thenSit: false });
        else {
          const [fx, fy] = dryTile(s.pond);
          Object.assign(pet, { tx: fx, ty: fy, thenNap: false, thenSit: false });
        }
      }
    } else if (pet.state === 'walk') {
      if (pet.thenNap) Object.assign(pet, { state: 'nap', timer: 24 + Math.random() * 24, thenNap: false });
      else if (pet.thenSit) Object.assign(pet, { state: 'sit', timer: 26 + Math.random() * 20, thenSit: false });
      else Object.assign(pet, { state: 'idle', timer: 6 + Math.random() * 14 });
    }
  };

  // one full repaint — cheap at this size, honest 8-bit steps
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const s = stateRef.current;
    if (!canvas || !s.plants) return;
    const ctx = canvas.getContext('2d');
    const w = world.current;
    // fullscreen upscale: paint in vw×vh logical px (the classic 272x208 when windowed,
    // the native internal resolution when fullscreen), the transform blows each up k×
    const vw = w.vw || W;
    const vh = w.vh || H;
    ctx.setTransform(w.px || 1, 0, 0, w.px || 1, 0, 0);
    const frame = w.frame++;
    // the ambient clock owns the scene: a full day each 5 minutes, weather every 2 —
    // palettes crossfade near stage boundaries so the light glides instead of snapping
    let amb = ambientNow();
    if (clockRef.current) {
      const df = clockRef.current.dayFrac ?? 0.5;
      let stg = 'night';
      for (const [name, at] of STAGE_EDGE) if (df >= at) stg = name;
      amb = { dayFrac: df, stage: stg, weather: 'sunny', slot: 0 };
    }
    if (driftRef.current) w.cam.yaw += driftRef.current * 0.2; // the draw tick is 200ms
    const [stFrom, stTo, stT] = stagePhase(amb.dayFrac);
    const stage = blendStage(stFrom, stTo, stT);
    const stName = stT < 0.5 ? stFrom : stTo;
    const weather = amb.weather; // scene only — the ECONOMY still reads the server's roll
    const night = stName === 'night';
    if (w.wslot !== amb.slot || w.warea !== vw * vh) {
      // a fresh weather mood (or a resized viewport): reseed the particle pools —
      // counts scale with the visible area so fullscreen rain stays just as dense
      w.wslot = amb.slot;
      w.warea = vw * vh;
      const area = (vw * vh) / (W * H);
      w.drops = Array.from({ length: Math.round((weather === 'storm' ? 52 : 26) * area) }, () => ({ x: Math.random() * vw, y: Math.random() * vh }));
      w.flakes = Array.from({ length: Math.round(20 * area) }, () => ({ x: Math.random() * vw, y: Math.random() * vh, p: Math.random() * 6 }));
      w.meteors = [];
    }
    const cam = camera(w.cam.yaw, w.cam.pitch, w.cam.dist, w.cam.hgt, vw, vh);
    const t = frame * 0.12;

    // ---- panorama: sky above the true horizon of the z=0 plane, meadow below ----
    const yh = Math.round(cam.horizonY);
    if (yh > 0) {
      const bh = Math.max(1, Math.ceil(yh / stage.sky.length));
      stage.sky.forEach((c, i) => {
        ctx.fillStyle = c;
        ctx.fillRect(0, i * bh, vw, Math.min(bh, yh - i * bh) + 1);
      });
    }

    // stars: seeded azimuth/elevation points, world-anchored — the sky turns with you
    if (stage.stars && yh > 0) {
      // the milky band: a faint river of far stars arcing across one axis of the sky
      // the low sky (el ≲0.5) is all a tilted lens ever frames — keep the stars living there
      const brnd = mulberry32(s.ownerSeed ^ 0xba9d);
      ctx.fillStyle = 'rgba(214,220,244,0.45)';
      for (let i = 0; i < 64; i++) {
        const az = brnd() * Math.PI * 2;
        const el = 0.34 + Math.sin(az + 0.8) * 0.3 + (brnd() - 0.5) * 0.14;
        if (el < 0.05) continue;
        const sd = cam.projectDir(Math.cos(el) * Math.cos(az), Math.cos(el) * Math.sin(az), Math.sin(el));
        if (sd && sd[1] < yh) ctx.fillRect(Math.round(sd[0]), Math.round(sd[1]), 1, 1);
      }
      const rnd = mulberry32(s.ownerSeed ^ 0x57a5);
      for (let i = 0; i < 60; i++) {
        const az = rnd() * Math.PI * 2;
        const el = 0.1 + rnd() * (i % 3 ? 0.55 : 1.1);
        const sd = cam.projectDir(Math.cos(el) * Math.cos(az), Math.cos(el) * Math.sin(az), Math.sin(el));
        if (sd && sd[1] < yh && (frame + i) % 8 < 6) {
          ctx.fillStyle = i % 5 ? '#e9edfb' : '#fff3c4';
          const su = i % 7 === 0 ? 2 : 1; // a few bright doubles
          ctx.fillRect(Math.round(sd[0]), Math.round(sd[1]), su, su);
        }
      }
    }

    // sun / moon ride a continuous arc derived from the ambient day — the light travels
    let sunPos = null;
    if ((weather !== 'cloudy' && weather !== 'storm') || night) {
      const { az: cAz, el } = celestial(amb.dayFrac);
      const sd = cam.projectDir(Math.cos(el) * Math.cos(cAz), Math.cos(el) * Math.sin(cAz), Math.sin(el));
      if (sd) {
        sunPos = sd;
        const sx = Math.round(sd[0]);
        const sy = Math.round(sd[1]);
        // halo first — two soft crosses of light behind the disc
        ctx.fillStyle = night ? 'rgba(226,228,240,0.10)' : 'rgba(255,224,150,0.14)';
        ctx.fillRect(sx - 9, sy - 5, 18, 10);
        ctx.fillRect(sx - 5, sy - 9, 10, 18);
        ctx.fillStyle = night ? 'rgba(226,228,240,0.16)' : 'rgba(255,224,150,0.2)';
        ctx.fillRect(sx - 6, sy - 3, 12, 6);
        ctx.fillRect(sx - 3, sy - 6, 6, 12);
        ctx.fillStyle = stage.sun;
        ctx.fillRect(sx - 4, sy - 2, 8, 4);
        ctx.fillRect(sx - 2, sy - 4, 4, 8);
        if (night) {
          ctx.fillStyle = stage.sky[0]; // bite the moon
          ctx.fillRect(sx - 4, sy - 4, 4, 4);
        }
      }
    }

    // fluffy cumulus billboards pinned to world azimuths, drifting a breath per minute —
    // painted before the ground so the ridge line can overlap them
    if (yh > 8) {
      const krnd = mulberry32(s.ownerSeed ^ 0xc10d);
      const puffy = weather === 'cloudy' || weather === 'rain' || weather === 'snow' || weather === 'storm';
      const nC = Math.round((puffy ? 7 : 3) * Math.max(1, vw / W)); // a wider sky earns more clouds
      ctx.fillStyle = night ? 'rgba(186,192,220,0.35)' : puffy ? 'rgba(240,243,250,0.85)' : 'rgba(255,255,255,0.8)';
      for (let i = 0; i < nC; i++) {
        const az = krnd() * Math.PI * 2 + frame * (0.0005 + krnd() * 0.0004);
        const el = 0.32 + krnd() * 0.5;
        const sz = 0.8 + krnd() * 0.9;
        const sd = cam.projectDir(Math.cos(el) * Math.cos(az), Math.cos(el) * Math.sin(az), Math.sin(el));
        if (!sd || sd[1] > yh - 4) continue;
        const cx = Math.round(sd[0]);
        const cy = Math.round(sd[1]);
        const cu = Math.max(2, Math.round(5 * sz));
        ctx.fillRect(cx - cu * 2, cy, cu * 4, cu);
        ctx.fillRect(cx - cu + (i % 2 ? -(cu >> 1) : cu >> 1), cy - cu, cu * 2, cu);
      }
    }

    // the meadow is FINITE now: a disc that ends behind the hill rings. below the horizon
    // we first lay ridge haze (the tone the hills stand on), then the ground disc on top —
    // so bare grass can never meet the sky beyond the ridgeline at any pitch/yaw/dolly
    const skyLo = stage.sky[stage.sky.length - 1];
    const beyond = mix(stage.hillFar, skyLo, 0.5);
    const gy = Math.max(0, yh);
    ctx.fillStyle = beyond;
    ctx.fillRect(0, gy, vw, vh - gy);
    groundDisc(ctx, cam, MEADOW_R, mix(stage.ground[0], beyond, 0.5)); // a soft rolled-off rim
    groundDisc(ctx, cam, MEADOW_R - 1.6, stage.ground[0]);

    // seeded two-tone mottling: flat world blobs dithering the meadow at every yaw
    const groundLite = mix(stage.ground[0], stage.tileA, 0.3);
    for (const m of s.mottle) {
      const c = cam.project(m.x, m.y, 0);
      if (!c || c[1] < yh - 1) continue;
      const mw = Math.min(26, Math.max(1, Math.round((FOCAL * m.w) / c[2])));
      const mh = Math.max(1, Math.round(mw * 0.4));
      ctx.fillStyle = m.tone ? stage.ground[1] : groundLite;
      ctx.fillRect(Math.round(c[0] - mw / 2), Math.round(c[1] - mh / 2), mw, mh);
    }

    // the ultimate-distance ridge: a hazed strip pinned to the horizon line so bare meadow
    // never meets bare sky no matter what the dolly/height/pitch does between ring gaps
    if (yh > 0 && yh < vh) {
      ctx.fillStyle = mix(stage.hillFar, skyLo, 0.55);
      ctx.fillRect(0, yh - 2, vw, 4);
      // low bumps riding the strip, panning with the yaw like everything else on the skyline
      const pan = Math.round(-w.cam.yaw * FOCAL);
      const nB = Math.round((12 * (vw + 24)) / (W + 24)); // exactly 12 on the classic canvas
      for (let i = 0; i < nB; i++) {
        const bx = ((((i * 53 + pan) % (vw + 24)) + vw + 24) % (vw + 24)) - 12;
        const bw = 14 + (i % 4) * 6;
        ctx.fillRect(bx, yh - 3 - (i % 3), bw, 2 + (i % 3));
        ctx.fillRect(bx + 3 + (i % 5), yh - 4 - (i % 3), bw - 6, 1); // a softer 1px shoulder
      }
    }

    // hill rings: seeded ridge silhouettes standing ON the meadow at world azimuths —
    // drawn as 2px columns with a seeded ±1px ridgeline jag, so the whole skyline lives
    // at the same 1-2px granularity as the meadow and the sprites (no more flat slabs)
    // which screen side the light sits on (+ = screen-right): the celestial azimuth vs
    // the camera yaw — morning and evening visibly swap the shaded faces as the sun crosses
    const sunSide = Math.sin(celestial(amb.dayFrac).az - w.cam.yaw);
    const RING_COL = [stage.hillNear, stage.hillFar, mix(stage.hillFar, skyLo, 0.4), mix(stage.hillFar, skyLo, 0.55)];
    const hillsOn = s.hills
      .map((hl) => ({ hl, p: cam.project(hl.x, hl.y, 0) }))
      .filter((o) => o.p && o.p[2] > 6) // a ridge the dolly moved past is skipped, not worn as a wall
      .sort((a, b) => b.p[2] - a.p[2]);
    for (const { hl, p } of hillsOn) {
      const pd = Math.max(p[2], 12); // a ridge sliding past the lens must not become a wall
      // atmospheric perspective: the farther the ridge, the closer its tone leans to the sky
      const haze = Math.min(0.68, hl.ring * 0.17 + Math.max(0, (p[2] - 26) / 120));
      const base = mix(RING_COL[hl.ring], skyLo, haze);
      const hw = (FOCAL * hl.wd) / pd / 2;
      const sh = (FOCAL * hl.h) / pd;
      const px0 = Math.round(p[0]);
      const py0 = Math.round(p[1]);
      if (px0 + hw < -2 || px0 - hw > vw + 2 || py0 < -2) continue;
      const shade = mix(base, '#22263a', 0.28);
      const snowC = mix('#eef3f8', skyLo, haze * 0.5);
      const jr = mulberry32(hl.jseed);
      const step = hw > 110 ? 3 : 2;
      const cols = Math.max(2, Math.ceil((hw * 2) / step));
      for (let ci = 0; ci <= cols; ci++) {
        const q = (ci / cols) * 2 - 1; // -1..1 across the hill
        const jag = (jr() - 0.5) * 3; // seeded ridgeline jitter — no 6px flat runs survive
        const colH = Math.max(0, Math.round(sh * Math.pow(Math.cos((Math.abs(q) * Math.PI) / 2), 0.9) + jag));
        if (!colH) continue;
        const cxp = px0 + Math.round(q * hw);
        if (cxp < -step || cxp > vw + step) continue;
        ctx.fillStyle = base;
        ctx.fillRect(cxp, py0 - colH, step, colH);
        // sun-tracked shading: the slope turned from the light falls into shadow —
        // continuous in f, with checker-teeth dither across the terminator instead of
        // a hard split, so the dark side glides (and swaps) as the sun travels
        if (hl.ring < 2 && hw > 10) {
          const f = Math.max(0, Math.min(1, (0.2 - q * sunSide) / 0.8)); // 0 lit .. 1 deep shade
          if (f > (ci % 2 ? 0.24 : 0.4)) {
            const shH = Math.round(colH * (0.18 + 0.32 * f + (ci % 2) * 0.1));
            ctx.fillStyle = shade;
            ctx.fillRect(cxp, py0 - shH, step, shH);
          }
        }
        if (hl.snow) {
          // the snowline is a jagged per-peak noise line, not a flat lid — with a
          // 1-2px dithered melt band where rock grizzles through at the boundary
          const snowAt = sh * (0.74 + (jr() - 0.5) * 0.16);
          if (colH > snowAt) {
            const capH = Math.max(1, Math.round(colH - snowAt));
            ctx.fillStyle = snowC;
            ctx.fillRect(cxp, py0 - colH, step, capH);
            // dither: odd columns drip one extra px of snow, even ones bite a px of rock back
            if (ci % 2) ctx.fillRect(cxp, py0 - colH + capH, step, 1);
            else if (capH > 1) {
              ctx.fillStyle = base;
              ctx.fillRect(cxp + (step >> 1), py0 - colH + capH - 1, Math.max(1, step >> 1), 1);
            }
          }
        }
      }
      // 1px speckle — rocks and shrubs freckling the nearer slopes
      if (hl.ring < 2 && hw > 12) {
        const nS = Math.min(16, Math.round((hw * sh) / 130));
        for (let i = 0; i < nS; i++) {
          const q = jr() * 1.7 - 0.85;
          const f = jr();
          const hAt = sh * Math.pow(Math.cos((Math.abs(q) * Math.PI) / 2), 0.9);
          ctx.fillStyle = i % 3 ? mix(base, '#22263a', 0.22) : mix(base, '#eef3f8', 0.18);
          ctx.fillRect(px0 + Math.round(q * hw), py0 - Math.round(hAt * (0.1 + f * 0.75)), 1, 1);
        }
      }
      if (hl.trees && hw > 22) {
        // tree specks on the lower slopes
        ctx.fillStyle = mix('#2f5c3a', base, 0.35);
        for (const [fx, fz] of hl.trees)
          ctx.fillRect(Math.round(p[0] + fx * hw * (1 - (0.08 + fz))), py0 - Math.round(sh * (0.08 + fz)) - 3, 2, 3);
      }
    }

    // a soft haze band breathing on the horizon line — thicker when the fog rolls in
    if (yh > 0 && yh < vh) {
      ctx.fillStyle = skyLo;
      ctx.globalAlpha = weather === 'fog' ? 0.4 : 0.16;
      ctx.fillRect(0, yh - 4, vw, 8);
      ctx.globalAlpha = weather === 'fog' ? 0.28 : 0.09;
      ctx.fillRect(0, yh - 8, vw, 16);
      ctx.globalAlpha = 1;
    }

    // ---- ground pass: the plot melts into the meadow — no hard soil frame ----
    for (const dc of s.decals) decalQuad(ctx, cam, dc, stage);
    const softB = mix(stage.tileB, stage.tileA, 0.35); // the checker whispers now
    const edgeTone = (c, ring) => (ring === 0 ? mix(c, stage.ground[0], 0.45) : ring === 1 ? mix(c, stage.ground[0], 0.18) : c);
    const tiles = [];
    for (let x = 0; x < GRID; x++)
      for (let y = 0; y < GRID; y++) {
        const c = cam.project(x, y, 0);
        if (c) tiles.push({ x, y, cx: c[0], cy: c[1], d: c[2] });
      }
    tiles.sort((a, b) => b.d - a.d);
    // the pond reflects the sky: its far rows lean toward the horizon tone and its
    // middle sinks darker — find the pond's screen extent for the vertical gradient
    let pyLo = 1e9;
    let pyHi = -1e9;
    for (const tl of tiles)
      if (s.pond.has(`${tl.x},${tl.y}`)) {
        pyLo = Math.min(pyLo, tl.cy);
        pyHi = Math.max(pyHi, tl.cy);
      }
    const eyeGx = CX + w.cam.dist * Math.cos(w.cam.pitch) * Math.cos(w.cam.yaw);
    const eyeGy = CX + w.cam.dist * Math.cos(w.cam.pitch) * Math.sin(w.cam.yaw);
    const mud = mix(stage.dirt[1], '#3a3226', 0.4);
    const sandC = mix(stage.dirt[0], '#dcc890', 0.55);
    // the recess, pass one: the whole earth bowl at ground level FIRST, so the sunken
    // water surfaces can then overlap each other seamlessly — only the outer far lip
    // (2-3px of bank) stays visible, and the pond reads as water sitting IN the land
    for (const tl of tiles) if (s.pond.has(`${tl.x},${tl.y}`)) tileQuad(ctx, cam, tl.x, tl.y, mud);
    for (const tl of tiles) {
      const k = `${tl.x},${tl.y}`;
      if (s.pond.has(k)) {
        const vf = pyHi > pyLo ? (pyHi - tl.cy) / (pyHi - pyLo) : 0.5; // 1 = far row
        let wc = mix(stage.water[(tl.x + tl.y + (frame >> 3)) % 2], skyLo, 0.16 + 0.3 * vf);
        const dr = Math.hypot(tl.x - s.pondC[0], tl.y - s.pondC[1]) / (s.pondR || 1);
        wc = mix(wc, '#16283c', 0.26 * Math.max(0, 1 - dr)); // deeper toward the middle
        tileQuad(ctx, cam, tl.x, tl.y, wc, WATER_Z);
      } else {
        // outer rings lean toward the meadow tone — grass-blended, not fenced
        const ring = Math.min(tl.x, tl.y, GRID - 1 - tl.x, GRID - 1 - tl.y);
        tileQuad(ctx, cam, tl.x, tl.y, edgeTone((tl.x + tl.y) % 2 ? softB : stage.tileA, ring));
        // shoreline: a damp mud lip on the near banks, dry sand on the far shore
        const dirs = s.sand.get(k);
        if (dirs)
          for (const [dx, dy] of dirs) {
            const facing = dx * (tl.x - eyeGx) + dy * (tl.y - eyeGy) > 0;
            edgeStrip(ctx, cam, tl.x, tl.y, dx, dy, facing ? mix(mud, '#241d14', 0.35) : sandC);
          }
        if (weather === 'snow') {
          const ts = FOCAL / tl.d;
          ctx.fillStyle = 'rgba(240,246,252,0.55)';
          ctx.fillRect(Math.round(tl.cx - ts * 0.33), Math.round(tl.cy) - 1, Math.max(3, Math.round(ts * 0.66)), 2);
        }
      }
    }
    // pond life: ripple rings bloom from random points every few seconds, 2-3 glints
    // drift across the surface, lily pads ride near the shore
    if (s.pondTiles.length) {
      if (frame % 17 === 0) {
        const [rx, ry] = s.pondTiles[Math.floor(Math.random() * s.pondTiles.length)];
        w.ripples.push({ x: rx + (Math.random() - 0.5) * 0.6, y: ry + (Math.random() - 0.5) * 0.6, age: 0 });
      }
      w.ripples = w.ripples.filter((r) => ++r.age <= 12);
      for (const r of w.ripples) {
        const wr = 0.08 + r.age * 0.05;
        ctx.fillStyle = `rgba(235,245,255,${(0.5 * (1 - r.age / 12)).toFixed(2)})`;
        for (let i = 0; i < 10; i++) {
          const aa = (i / 10) * Math.PI * 2 + r.age * 0.1;
          const dx = r.x + Math.cos(aa) * wr;
          const dy = r.y + Math.sin(aa) * wr;
          if (!s.pond.has(`${Math.round(dx)},${Math.round(dy)}`)) continue; // rings die at the bank
          const c = cam.project(dx, dy, WATER_Z);
          if (c) ctx.fillRect(Math.round(c[0]), Math.round(c[1]), 1, 1);
        }
      }
      for (let i = 0; i < 3; i++) {
        const ga = t * (0.05 + i * 0.023) + i * 2.4;
        const gwx = s.pondC[0] + Math.cos(ga) * s.pondR * 0.55;
        const gwy = s.pondC[1] + Math.sin(ga * 1.31) * s.pondR * 0.5;
        if (!s.pond.has(`${Math.round(gwx)},${Math.round(gwy)}`)) continue;
        const c = cam.project(gwx, gwy, WATER_Z);
        if (!c) continue;
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillRect(Math.round(c[0]), Math.round(c[1]), 2, 1);
      }
      for (const li of s.lilies) {
        const c = cam.project(li.x, li.y, WATER_Z);
        if (!c) continue;
        const lu = Math.max(2, Math.round((FOCAL * 0.14) / c[2]));
        ctx.fillStyle = '#3f9a55';
        ctx.fillRect(Math.round(c[0] - lu), Math.round(c[1]) - (lu >> 1), lu * 2, Math.max(2, lu));
        ctx.fillStyle = '#2f7d43';
        ctx.fillRect(Math.round(c[0]), Math.round(c[1]) - (lu >> 1), Math.max(1, lu >> 1), Math.max(1, lu >> 1));
      }
    }

    // placement hint: pulse the free land when planting or moving
    if (modeRef.current && frame % 2) {
      ctx.fillStyle = 'rgba(255,255,255,0.14)';
      for (let y = 0; y < GRID; y++)
        for (let x = 0; x < GRID; x++) {
          const k = `${x},${y}`;
          if (s.pond.has(k) || s.plants.some((p) => p.px === x && p.py === y)) continue;
          const c = cam.project(x, y, 0);
          if (!c) continue;
          const u = Math.max(1, Math.round(spriteScale(c[2]) * 2));
          ctx.fillRect(Math.round(c[0]) - (u >> 1), Math.round(c[1]) - (u >> 1), u, u);
        }
    }

    // ---- billboard pass: ONE depth-sorted list, far first — no more layer bugs ----
    const drag = w.drag;
    const bill = [];
    for (const p of s.plants)
      if (!(drag && drag.moved && p.id === drag.plant.id)) bill.push({ kind: 'plant', p, x: p.px, y: p.py, z: 0 });
    if (w.pet) bill.push({ kind: 'pet', x: w.pet.x, y: w.pet.y, z: 0 });
    if (w.bobber) bill.push({ kind: 'bobber', x: w.bobber.x, y: w.bobber.y, z: WATER_Z }); // riding the recessed surface
    w.houseWin = null; // set again only if the cottage survives projection this frame
    if (s.house) bill.push({ kind: 'house', x: s.house.x, y: s.house.y, z: 0 });
    s.butterflies.forEach((b) => {
      if (b.fading && frame % 4 >= 2) return; // last hours: an intermittent shimmer
      const [bx, by, bz] = flutter(b, t);
      bill.push({ kind: 'butterfly', b, x: bx, y: by, z: bz });
    });
    for (const dt of s.detail) bill.push({ kind: 'detail', dt, x: dt.x, y: dt.y, z: 0 });
    if (night && !['rain', 'snow', 'storm', 'fog'].includes(weather)) {
      // fireflies: seeded world anchors on the meadow, blinking as they drift (fog smothers them)
      const rnd = mulberry32(s.ownerSeed ^ 0xf1fe);
      for (let i = 0; i < 6; i++) {
        const az = rnd() * Math.PI * 2;
        const rr = 5.5 + rnd() * 6;
        if ((frame + i * 2) % 7 >= 4) continue;
        bill.push({
          kind: 'fly',
          x: CX + Math.cos(az) * rr + Math.sin(t + i * 2.1) * 0.6,
          y: CX + Math.sin(az) * rr + Math.cos(t * 0.7 + i) * 0.4,
          z: 0.5 + Math.sin(t * 0.9 + i) * 0.2,
        });
      }
    }
    const drawList = [];
    for (const b of bill) {
      const c = cam.project(b.x, b.y, b.z);
      if (c) drawList.push({ ...b, sx: c[0], sy: c[1], d: c[2] });
    }
    drawList.sort((a, b) => b.d - a.d);
    for (const sp of drawList) {
      const sc = spriteScale(sp.d); // near-big-far-small, clamped 0.6..1.6
      if (sp.kind === 'plant') {
        drawPlant(ctx, sp.p, sp.sx, sp.sy, frame, sc, s.fruitReady && sp.p.kind === 'tree');
      } else if (sp.kind === 'pet') {
        const hopY = w.pet.state === 'hop' ? [0, -3, -5, -3][frame % 4] * sc : 0;
        const wigX = w.pet.state === 'wiggle' ? (frame % 2 ? 1 : -1) : 0;
        blobShadow(ctx, sp.sx, sp.sy + 1, 7 * sc, 2.1 * sc);
        drawBuddy(ctx, petSeedFor(s.username), sp.sx + wigX, sp.sy + hopY, frame, w.pet.state === 'nap', petMetaRef.current, 2 * sc);
      } else if (sp.kind === 'bobber') {
        // the bobber: a cast line waiting on the pond, then a happy splash
        w.bobber.t = (w.bobber.t ?? 0) + 1;
        const bt = w.bobber.t;
        const bx = Math.round(sp.sx);
        const by = Math.round(sp.sy);
        blobShadow(ctx, bx, by + 1, 4 * sc, 1.3 * sc, 0.1);
        if (w.bobber.caught) {
          ctx.fillStyle = '#eaf4ff';
          for (let i = 0; i < 5; i++)
            ctx.fillRect(bx + Math.round((-6 + i * 3) * sc), by - Math.round([0, 3, 5, 3, 0][i] * sc) - (bt % 3), 2, 2);
        } else {
          ctx.fillStyle = '#d85a30';
          ctx.fillRect(bx - 1, by - 2 + (bt % 4 < 2 ? 0 : 1), Math.max(2, Math.round(3 * sc)), Math.max(2, Math.round(3 * sc)));
          ctx.fillStyle = 'rgba(255,255,255,0.55)';
          const ring = Math.round((3 + (bt % 8)) * sc);
          ctx.fillRect(bx - ring, by, ring * 2, 1);
        }
      } else if (sp.kind === 'butterfly') {
        const u = Math.max(1, Math.round(sc));
        const flap = frame % 2 ? 2 : 1;
        const bx = Math.round(sp.sx);
        const by = Math.round(sp.sy);
        ctx.fillStyle = sp.b.hue[frame % 2 ? 0 : 1];
        ctx.fillRect(bx - flap * u, by, flap * u, 2 * u);
        ctx.fillRect(bx + u, by, flap * u, 2 * u);
        ctx.fillStyle = '#232326';
        ctx.fillRect(bx, by, u, 2 * u);
      } else if (sp.kind === 'detail') {
        drawDetail(ctx, sp.dt, sp.sx, sp.sy, sc, stage);
      } else if (sp.kind === 'house') {
        drawHouse(ctx, sp.sx, sp.sy, sp.d, night, w);
      } else if (sp.kind === 'fly') {
        ctx.fillStyle = '#ffe9a0';
        ctx.fillRect(Math.round(sp.sx), Math.round(sp.sy), 2, 2);
      }
    }

    // Tyndall shafts: a low dawn/dusk sun throws a few quiet beams down-forward.
    // 'screen' at whisper alpha — skipped entirely at night and midday
    if (sunPos && (stName === 'dawn' || stName === 'dusk') && (weather === 'sunny' || weather === 'fog') &&
        sunPos[0] > -16 && sunPos[0] < vw + 16 && sunPos[1] < yh + 4) {
      ctx.globalCompositeOperation = 'screen';
      ctx.fillStyle = stName === 'dawn' ? 'rgba(255,224,170,0.07)' : 'rgba(255,190,130,0.08)';
      for (let i = 0; i < 4; i++) {
        const ang = 0.62 + i * 0.24 + Math.sin(t * 0.05 + i * 1.7) * 0.02; // a fan, breathing slowly
        ctx.save();
        ctx.translate(sunPos[0], sunPos[1]);
        ctx.rotate(sunPos[0] > vw / 2 ? Math.PI - ang : ang); // always fanning back across the scene
        ctx.fillRect(8, -(2 + (i % 2) * 2), 70 + (i % 3) * 30, 4 + (i % 2) * 3);
        ctx.restore();
      }
      ctx.globalCompositeOperation = 'source-over';
    }

    // weather particles (screen-space, in front of the world)
    if (weather === 'rain' || weather === 'storm') {
      const stormy = weather === 'storm';
      ctx.fillStyle = stormy ? 'rgba(140,175,215,0.8)' : 'rgba(122,168,220,0.75)';
      for (const d of w.drops) {
        d.y += stormy ? 10 : 7;
        d.x += stormy ? 2.6 : 1.4;
        if (d.y > vh) { d.y = -6; d.x = Math.random() * vw; }
        if (d.x > vw + 4) d.x -= vw + 8;
        ctx.fillRect(Math.round(d.x), Math.round(d.y), 1, stormy ? 6 : 4);
      }
    } else if (weather === 'fog') {
      // two-three wide soft bands drifting at different depths — ragged pixel edges,
      // far hills mostly swallowed, the plot still readable
      const nF = Math.round((9 * (vw + 34)) / (W + 34)); // exactly 9 on the classic canvas
      for (let i = 0; i < 3; i++) {
        const a = [0.15, 0.11, 0.08][i];
        const bh = [14, 20, 28][i];
        const by = Math.round(Math.min(vh - 26, Math.max(4, yh)) - 8 + i * 26);
        const dx = Math.round(frame * [0.5, 0.9, 1.4][i]) * (i % 2 ? -1 : 1);
        ctx.fillStyle = `rgba(226,230,238,${a})`;
        ctx.fillRect(0, by, vw, bh);
        for (let k = 0; k < nF; k++) {
          const tx = ((((k * 34 + dx) % (vw + 34)) + vw + 34) % (vw + 34)) - 17;
          ctx.fillRect(tx, by - 3, 20, 3);
          ctx.fillRect(((tx + 17) % (vw + 34)) - 8, by + bh, 16, 3);
        }
      }
    } else if (weather === 'snow') {
      ctx.fillStyle = 'rgba(244,248,252,0.9)';
      for (const f of w.flakes) {
        f.y += 1.1; f.x += Math.sin((frame + f.p) * 0.4) * 0.8;
        if (f.y > vh) { f.y = -2; f.x = Math.random() * vw; }
        ctx.fillRect(Math.round(f.x), Math.round(f.y), 2, 2);
      }
    } else if (weather === 'meteor' && night) {
      if (frame % 36 === 0) w.meteors.push({ x: 30 + Math.random() * (vw - 90), y: 6 + Math.random() * 26, life: 7 });
      w.meteors = w.meteors.filter((m) => m.life-- > 0);
      for (const m of w.meteors) {
        m.x += 9; m.y += 4;
        ctx.fillStyle = '#fff3c4';
        ctx.fillRect(m.x, m.y, 3, 1);
        ctx.fillStyle = 'rgba(255,243,196,0.45)';
        ctx.fillRect(m.x - 5, m.y - 2, 4, 1);
      }
    }

    // ---- global light: multiply-tint the whole painted world by stage & weather ----
    const wtint =
      weather === 'rain' ? 'rgba(110,130,165,0.20)'
      : weather === 'storm' ? 'rgba(84,96,132,0.30)'
      : weather === 'fog' ? 'rgba(198,202,212,0.20)'
      : weather === 'snow' ? 'rgba(200,212,232,0.16)' : null;
    const tintA = stage.tint[3];
    if (tintA > 0.01 || wtint) {
      ctx.globalCompositeOperation = 'multiply';
      if (tintA > 0.01) {
        ctx.fillStyle = `rgba(${Math.round(stage.tint[0])},${Math.round(stage.tint[1])},${Math.round(stage.tint[2])},${tintA.toFixed(3)})`;
        ctx.fillRect(0, 0, vw, vh);
      }
      if (wtint) { ctx.fillStyle = wtint; ctx.fillRect(0, 0, vw, vh); }
      ctx.globalCompositeOperation = 'source-over';
    }
    if (stage.dim > 0.01) {
      ctx.fillStyle = `rgba(8,10,26,${stage.dim.toFixed(3)})`;
      ctx.fillRect(0, 0, vw, vh);
    }

    // storm lightning: a seeded frame schedule — one 2-frame flash every ~6-14s, never dice per frame
    if (weather === 'storm') {
      const seg = Math.floor(frame / 50);
      const hit = seg * 50 + 15 + Math.floor(mulberry32((s.ownerSeed ^ Math.imul(seg + 1, 0x9e3779b1)) >>> 0)() * 20);
      if (frame === hit || frame === hit + 1) {
        const bright = frame === hit;
        if (bright && yh > 12) {
          // one jagged bolt stabbing through the sky band
          const brnd = mulberry32((s.ownerSeed ^ seg) >>> 0);
          let bx = 30 + brnd() * (vw - 60);
          let by = 2;
          ctx.fillStyle = '#f2f6ff';
          for (let k = 0; k < 4; k++) {
            const ny = by + (yh - 6) / 4;
            const nx = bx + (brnd() - 0.5) * 26;
            for (let q = 0; q < 5; q++)
              ctx.fillRect(Math.round(bx + ((nx - bx) * q) / 5), Math.round(by + ((ny - by) * q) / 5), 2, Math.ceil((ny - by) / 5) + 1);
            bx = nx;
            by = ny;
          }
        }
        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = `rgba(255,255,255,${bright ? 0.28 : 0.1})`;
        ctx.fillRect(0, 0, vw, vh);
        ctx.globalCompositeOperation = 'source-over';
      }
    }

    // the cottage window glows warm THROUGH the night tint
    if (w.houseWin) {
      const hw = w.houseWin;
      ctx.fillStyle = 'rgba(255,214,120,0.22)';
      ctx.fillRect(hw.x - hw.u, hw.y - hw.u, hw.w + 2 * hw.u, hw.h + 2 * hw.u);
      ctx.fillStyle = '#ffe2a1';
      ctx.fillRect(hw.x, hw.y, hw.w, hw.h);
    }

    // ---- UI feedback rides ON TOP of the light so it always reads ----

    // selection brackets around the chosen plant
    const sel = selectedRef.current && s.plants.find((p) => p.id === selectedRef.current.id);
    if (sel) {
      const c = cam.project(sel.px, sel.py, 0);
      if (c) {
        const sc = spriteScale(c[2]);
        const cx = Math.round(c[0]);
        const cy = Math.round(c[1]);
        const rr = Math.round(10 * sc);
        ctx.fillStyle = frame % 2 ? '#ffffff' : '#f5c542';
        ctx.fillRect(cx - rr - 3, cy - 1, 3, 1);
        ctx.fillRect(cx + rr, cy - 1, 3, 1);
        ctx.fillRect(cx - 1, cy - Math.round(6 * sc) - 2, 2, 1);
        ctx.fillRect(cx - 1, cy + Math.round(4 * sc), 2, 1);
      }
    }

    // hover halo — pulsing ticks beneath the pointed-at billboard
    const hov = w.hover;
    if (hov && !w.drag && !w.camDrag) {
      let hx = null;
      let hy = null;
      if (hov.kind === 'plant') {
        const hp = s.plants.find((p) => p.id === hov.p.id);
        if (hp) { hx = hp.px; hy = hp.py; }
      } else if (hov.kind === 'pet' && w.pet) {
        hx = w.pet.x;
        hy = w.pet.y;
      }
      if (hx != null) {
        const c = cam.project(hx, hy, 0);
        if (c) {
          const sc = spriteScale(c[2]);
          const cx = Math.round(c[0]);
          const cy = Math.round(c[1]);
          const rr = Math.round(8 * sc);
          ctx.fillStyle = frame % 2 ? 'rgba(255,255,255,0.85)' : 'rgba(53,208,127,0.85)';
          ctx.fillRect(cx - rr - 3, cy, 4, 1);
          ctx.fillRect(cx + rr, cy, 4, 1);
          ctx.fillRect(cx - 2, cy - Math.round(4 * sc), 4, 1);
          ctx.fillRect(cx - 2, cy + Math.round(3 * sc), 4, 1);
        }
      }
    }

    // pats become hearts
    w.hearts = w.hearts.filter((hh) => hh.age-- > 0);
    for (const hh of w.hearts) {
      hh.y -= 1.5;
      ctx.fillStyle = hh.age % 4 < 2 ? '#f4c0d1' : '#d4537e';
      ctx.fillRect(Math.round(hh.x), Math.round(hh.y), 2, 2);
      ctx.fillRect(Math.round(hh.x) + 3, Math.round(hh.y), 2, 2);
      ctx.fillRect(Math.round(hh.x) + 1, Math.round(hh.y) + 2, 3, 2);
    }

    // drag ghost rides the pointer
    if (drag && drag.moved) {
      ctx.globalAlpha = 0.75;
      drawPlant(ctx, drag.plant, drag.gx, drag.gy, frame, 1);
      ctx.globalAlpha = 1;
    }

    tickPet(s);
  }, []);

  useEffect(() => {
    const iv = setInterval(draw, 200); // 5fps — proudly steppy
    return () => clearInterval(iv);
  }, [draw]);

  // fullscreen 2.0: NATIVE resolution. the backing store covers the real device pixels
  // and the world paints at an integer scale k — internal resolution vw×vh = screen/k,
  // so wider screens genuinely see a wider FOV instead of a stretched 272. windowed
  // mode keeps the classic 272x208. every logical px stays a perfect k×k device block
  // (≤k-1 device px of letterbox absorbs the remainder); toLocal maps pointer events
  // through vw/vh, so every control just works at either size.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const w = world.current;
    const size = () => {
      if (fs) {
        const dpr = window.devicePixelRatio || 1;
        const sw = Math.round(window.innerWidth * dpr);
        const sh = Math.round(window.innerHeight * dpr);
        const k = Math.max(2, Math.floor(Math.min(sw / W, sh / H)));
        w.px = k;
        w.vw = Math.floor(sw / k);
        w.vh = Math.floor(sh / k);
        cv.width = w.vw * k;
        cv.height = w.vh * k;
        cv.style.width = `${(w.vw * k) / dpr}px`;
        cv.style.height = `${(w.vh * k) / dpr}px`;
        cv.style.border = 'none';
      } else {
        w.px = 1;
        w.vw = W;
        w.vh = H;
        cv.width = W;
        cv.height = H;
        cv.style.width = '';
        cv.style.height = '';
        cv.style.border = '';
      }
      draw(); // repaint now — the 5fps tick is up to 200ms away
    };
    size();
    if (!fs) return;
    window.addEventListener('resize', size); // fullscreen transition can settle late
    return () => window.removeEventListener('resize', size);
  }, [fs, draw]);

  // ---- pointer interactions ----
  const toLocal = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const w = world.current;
    return [((e.clientX - rect.left) * (w.vw || W)) / rect.width, ((e.clientY - rect.top) * (w.vh || H)) / rect.height];
  };
  const plantAt = (tx, ty) => stateRef.current.plants?.find((p) => p.px === tx && p.py === ty);
  const freeLand = (tx, ty) =>
    tx >= 0 && tx < GRID && ty >= 0 && ty < GRID && !stateRef.current.pond.has(`${tx},${ty}`) && !plantAt(tx, ty);
  const pointToTile = (mx, my) => {
    const w = world.current;
    const c = w.cam;
    const g = camera(c.yaw, c.pitch, c.dist, c.hgt, w.vw, w.vh).unproject(mx, my);
    return g ? [Math.round(g[0]), Math.round(g[1])] : [-99, -99];
  };

  // one hit-test rules hover AND clicks: nearest billboard anchor within its
  // scaled sprite bounds beats the tile under the cursor, so what lights up
  // is exactly what a tap acts on — and it agrees with the projection
  const hitTest = (mx, my) => {
    const s = stateRef.current;
    const w = world.current;
    if (!s.plants) return { kind: 'none' };
    const cam = camera(w.cam.yaw, w.cam.pitch, w.cam.dist, w.cam.hgt, w.vw, w.vh);
    // the ripe fruit outranks everything — it hangs above the tree's own hit zone
    if (s.fruitReady) {
      const tree = s.plants.find((p) => p.kind === 'tree');
      if (tree) {
        const c = cam.project(tree.px, tree.py, 0);
        if (c) {
          const sc = spriteScale(c[2]);
          const [ux, uy] = fruitSpot(tree.seed);
          if (Math.hypot(mx - (c[0] + ux * 2 * sc), my - (c[1] - uy * 2 * sc)) <= 7 * sc) return { kind: 'fruit' };
        }
      }
    }
    let best = null;
    for (const p of s.plants) {
      const c = cam.project(p.px, p.py, 0);
      if (!c) continue;
      const sc = spriteScale(c[2]);
      const d = Math.hypot(mx - c[0], my - (c[1] - 5 * sc));
      if (d <= 12 * sc && (!best || d < best.d)) best = { kind: 'plant', p, d };
    }
    if (w.pet) {
      const c = cam.project(w.pet.x, w.pet.y, 0);
      if (c) {
        const sc = spriteScale(c[2]);
        const d = Math.hypot(mx - c[0], my - (c[1] - 6 * sc));
        if (d <= 14 * sc && (!best || d < best.d)) best = { kind: 'pet', d };
      }
    }
    if (best) return best;
    // ground first (land occludes the recess), then the sunken water plane — so
    // fishing taps land on the pond tile the eye actually sees, lip and all
    const g = cam.unproject(mx, my);
    if (g) {
      const tx = Math.round(g[0]);
      const ty = Math.round(g[1]);
      if (tx >= 0 && tx < GRID && ty >= 0 && ty < GRID && !s.pond.has(`${tx},${ty}`)) return { kind: 'tile', tx, ty };
    }
    const gw = cam.unproject(mx, my, WATER_Z);
    if (gw) {
      const tx = Math.round(gw[0]);
      const ty = Math.round(gw[1]);
      if (tx >= 0 && tx < GRID && ty >= 0 && ty < GRID && s.pond.has(`${tx},${ty}`)) return { kind: 'tile', tx, ty };
    }
    if (g) {
      // the bank ring itself still counts as the pond tile it wraps
      const tx = Math.round(g[0]);
      const ty = Math.round(g[1]);
      if (tx >= 0 && tx < GRID && ty >= 0 && ty < GRID) return { kind: 'tile', tx, ty };
    }
    return { kind: 'none' };
  };

  const onPointerDown = (e) => {
    if (!stateRef.current.plants) return;
    if (driftRef.current) return; // demo mode — the camera drives itself
    const w = world.current;
    if (w.gid != null) return; // one gesture at a time — extra fingers wait their turn
    w.gid = e.pointerId;
    try {
      canvasRef.current.setPointerCapture?.(e.pointerId);
    } catch {}
    const [mx, my] = toLocal(e);
    // middle button: vertical drag slides the eye up and down the mast (no autoscroll)
    if (e.button === 1) {
      e.preventDefault();
      w.hgtDrag = { y: my, h0: w.cam.hgt };
      return;
    }
    const hit = hitTest(mx, my);
    if (self && modeRef.current === 'plant') {
      const [tx, ty] = pointToTile(mx, my);
      if (freeLand(tx, ty)) onPlanted?.(tx, ty);
      else onToast?.('pick a free patch of grass');
      return;
    }
    if (self && modeRef.current === 'move') {
      const [tx, ty] = pointToTile(mx, my);
      if (freeLand(tx, ty)) onMovedTo?.(tx, ty);
      else onToast?.('that spot is taken — pick a free tile');
      return;
    }
    // the ripe fruit: one tap, one harvest — wired exactly like the pond
    if (hit.kind === 'fruit' && self && onHarvest) {
      Promise.resolve(onHarvest())
        .then((ok) => {
          if (ok) stateRef.current.fruitReady = false; // gone until the next ripening
        })
        .catch(() => {});
      return;
    }
    // plants win ties — water and merges are resources, pats are free
    if (hit.kind === 'plant' && self) {
      w.drag = { plant: hit.p, gx: mx, gy: my, moved: false, sx: mx, sy: my };
      return;
    }
    if (hit.kind === 'pet' && w.pet) {
      onSelect?.(null);
      const c = camera(w.cam.yaw, w.cam.pitch, w.cam.dist, w.cam.hgt, w.vw, w.vh).project(w.pet.x, w.pet.y, 0);
      w.pet.state = 'pat';
      w.pet.timer = 6;
      if (c) for (let i = 0; i < 4; i++) w.hearts.push({ x: c[0] - 6 + i * 4, y: c[1] - 22 - i * 2, age: 10 + i * 3 });
      onPat?.();
      return;
    }
    // the pond bites: tapping open water casts a line (self, no tool armed)
    if (hit.kind === 'tile' && self && !modeRef.current && stateRef.current.pond.has(`${hit.tx},${hit.ty}`) && onFish) {
      if (!w.bobber) {
        w.bobber = { x: hit.tx, y: hit.ty, t: 0 };
        Promise.resolve(onFish())
          .then((ok) => {
            if (!ok) {
              w.bobber = null;
              return;
            }
            if (w.bobber) w.bobber.caught = true;
            setTimeout(() => {
              w.bobber = null;
            }, 1600);
          })
          .catch(() => {
            w.bobber = null; // a snapped line must never brick the pond
          });
      }
      return;
    }
    // anything non-interactive grabs the WORLD: unproject the cursor to its ground
    // point and hold that exact point under the finger for the whole drag — pulling
    // the meadow itself. a sky grab (no ground hit) falls back to the old rate orbit.
    const grab = camera(w.cam.yaw, w.cam.pitch, w.cam.dist, w.cam.hgt, w.vw, w.vh).unproject(mx, my);
    w.camDrag = { x: mx, y: my, yaw0: w.cam.yaw, pitch0: w.cam.pitch, moved: false, grab };
  };

  const onPointerMove = (e) => {
    const w = world.current;
    if (w.gid != null && e.pointerId !== w.gid) return;
    const [mx, my] = toLocal(e);
    if (w.hgtDrag) {
      if (e.buttons & 4) w.cam.hgt = Math.max(HGT_MIN, Math.min(HGT_MAX, w.hgtDrag.h0 + (w.hgtDrag.y - my) * 0.04));
      return;
    }
    if (w.camDrag) {
      const cd = w.camDrag;
      if (cd.grab) {
        // grab semantics: re-solve yaw/pitch so the grabbed ground point rides the
        // cursor — near ground moves less per px than far ground, all by itself
        grabSolve(w.cam, cd.grab, mx, my, w.vw, w.vh);
      } else {
        // sky grab: ~0.008 rad per display px — canvas px are ~1.5x display px at panel width
        w.cam.yaw = cd.yaw0 + (mx - cd.x) * 0.012;
        w.cam.pitch = clampPitch(cd.pitch0 + (my - cd.y) * 0.012);
      }
      if (Math.abs(mx - cd.x) + Math.abs(my - cd.y) > 4) cd.moved = true;
      return;
    }
    const drag = w.drag;
    if (drag) {
      drag.gx = mx;
      drag.gy = my;
      if (Math.abs(mx - drag.sx) + Math.abs(my - drag.sy) > 6) drag.moved = true;
      return;
    }
    // idle pointer: remember what it rests on; the draw loop paints the halo
    const hit = hitTest(mx, my);
    w.hover = hit;
    let cur = 'default';
    if (hit.kind === 'pet') cur = 'pointer';
    else if (hit.kind === 'fruit' && self && onHarvest) cur = 'pointer';
    else if (hit.kind === 'plant' && self) cur = 'pointer';
    else if (hit.kind === 'tile' && self) {
      if (modeRef.current) cur = 'pointer';
      else if (onFish && stateRef.current.pond?.has(`${hit.tx},${hit.ty}`)) cur = 'pointer';
    }
    if (canvasRef.current) canvasRef.current.style.cursor = cur;
  };

  const onPointerLeave = () => {
    world.current.hover = null;
  };

  const onPointerUp = (e) => {
    const w = world.current;
    if (w.gid != null && e.pointerId !== w.gid) return;
    w.gid = null;
    if (w.hgtDrag) {
      w.hgtDrag = null;
      return;
    }
    if (w.camDrag) {
      if (!w.camDrag.moved) onSelect?.(null); // a motionless tap still clears the selection
      w.camDrag = null;
      return;
    }
    const drag = w.drag;
    w.drag = null;
    if (!drag) return;
    const [mx, my] = toLocal(e);
    if (!drag.moved) {
      onSelect?.(drag.plant); // a click selects — the action bar takes it from here
      return;
    }
    const [tx, ty] = pointToTile(mx, my);
    if (tx === drag.plant.px && ty === drag.plant.py) return;
    const target = plantAt(tx, ty);
    if (target && target.kind === drag.plant.kind && target.id !== drag.plant.id) {
      onAct?.({ type: 'merge', target, dragged: drag.plant });
    } else if (!target && freeLand(tx, ty)) {
      onAct?.({ type: 'move', plant: drag.plant, px: tx, py: ty });
    }
  };

  // a cancelled gesture (browser stole the pointer) must not leave a half-drag behind
  const onPointerCancel = (e) => {
    const w = world.current;
    if (w.gid != null && e.pointerId !== w.gid) return;
    w.gid = null;
    w.camDrag = null;
    w.hgtDrag = null;
    w.drag = null;
  };

  // mouse wheel dollies the lens in and out (the page must not scroll under it)
  const onWheel = (e) => {
    if (driftRef.current) return; // demo mode: let the page scroll past the hero
    e.preventDefault();
    const cam = world.current.cam;
    cam.dist = Math.max(DIST_MIN, Math.min(DIST_MAX, cam.dist + (e.deltaY > 0 ? 1.2 : -1.2)));
  };

  return html`<canvas
    ref=${canvasRef}
    class="pixel gardencanvas ${self ? 'mine' : ''}"
    width=${W}
    height=${H}
    onPointerDown=${onPointerDown}
    onPointerMove=${onPointerMove}
    onPointerUp=${onPointerUp}
    onPointerCancel=${onPointerCancel}
    onPointerLeave=${onPointerLeave}
    onWheel=${onWheel}
  />`;
}

// a mini plant from the flat sprite set — the guide's growth-path glyphs
function MiniPlant({ kind, cell = 3 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) drawMiniPlant(ref.current, hashString(`vb:guide:${kind}`), kind, { cell });
  }, [kind, cell]);
  return html`<canvas ref=${ref} class="pixel" width=${8 * cell} height=${8 * cell}
    style="width:${8 * cell}px;height:${8 * cell}px" />`;
}

// the buddy wearing an outfit — the harvest card's live mirror
function OutfitPreview({ username, meta, equip, cell = 7 }) {
  const ref = useRef(null);
  const eqKey = JSON.stringify(equip ?? null);
  useEffect(() => {
    if (!ref.current) return;
    return animateSprite(ref.current, petSeedFor(username), {
      cell,
      stage: meta?.stage ?? 'adult',
      wins: meta?.wins ?? 0,
      happiness: meta?.happiness ?? 'content',
      equip,
    });
  }, [username, cell, eqKey, meta?.stage, meta?.wins]);
  return html`<canvas ref=${ref} class="pixel" width=${SPRITE_W * cell} height=${SPRITE_H * cell}
    style="width:${SPRITE_W * cell}px;height:${SPRITE_H * cell}px" />`;
}

const FRUIT_ODDS = [['common', '60%'], ['uncommon', '27%'], ['rare', '10%'], ['legendary', '3%']];

// ---- the panel: toolbar + canvas + share (inline on Me tab; overlay for friends) ----

export function GardenPanel({ me, username, self = true, version = 0, onToast }) {
  const [data, setData] = useState(null);
  const [visits, setVisits] = useState([]);
  const [denied, setDenied] = useState(false);
  const [mode, setMode] = useState(null); // null | 'plant' | 'move'
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [petMeta, setPetMeta] = useState(null);
  const [shareCard, setShareCard] = useState(null); // {url, blob, text}
  const [aquarium, setAquarium] = useState(false); // the collection center, opened at the fish dex
  const [guide, setGuide] = useState(false);
  const [tut, setTut] = useState(() => self && typeof localStorage !== 'undefined' && !localStorage.getItem('vb:garden-hello'));
  const [fs, setFs] = useState(false);
  const [hud, setHud] = useState(true); // fullscreen HUD — auto-hides after ~3s idle
  const [fsHint, setFsHint] = useState(false); // first-entry 'press ESC to leave'
  const patSent = useRef(false);
  const wrapRef = useRef(null);
  const camRef = useRef(null);
  const who = username ?? me.username;

  // ESC is handled by the fullscreen API itself — we only mirror the state back
  // (and restore the normal layout) whenever it flips, however it flipped
  useEffect(() => {
    const onFsc = () => setFs(document.fullscreenElement === wrapRef.current);
    document.addEventListener('fullscreenchange', onFsc);
    return () => document.removeEventListener('fullscreenchange', onFsc);
  }, []);

  // fullscreen HUD sleeps after ~3s of pointer stillness, wakes on any move
  useEffect(() => {
    if (!fs) return;
    const el = wrapRef.current;
    let tm;
    const poke = () => {
      setHud(true);
      clearTimeout(tm);
      tm = setTimeout(() => setHud(false), 3000);
    };
    poke();
    el?.addEventListener('pointermove', poke);
    return () => {
      clearTimeout(tm);
      el?.removeEventListener('pointermove', poke);
      setHud(true);
    };
  }, [fs]);

  const enterFs = async () => {
    try {
      await wrapRef.current?.requestFullscreen?.();
      if (!localStorage.getItem('vb:garden-fs-hint')) {
        localStorage.setItem('vb:garden-fs-hint', '1');
        setFsHint(true);
        setTimeout(() => setFsHint(false), 3500);
      }
    } catch {
      onToast?.('fullscreen said no — the browser blocked it');
    }
  };

  // visiting kindnesses — the visit water pours from the server's own can (free,
  // 1/day, 2/day for mutual bests); nobody's charges move. butterflies linger a day.
  const visitAct = async (action, okText) => {
    const path = action === 'pat' ? `/api/pet/${who}/pat` : `/api/garden/${who}/visit`;
    const r = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(action === 'pat' ? {} : { action }),
    }).catch(() => null);
    const d = (await r?.json().catch(() => ({}))) ?? {};
    onToast?.(r?.ok ? okText : d.error ?? 'hm, that did not take');
    if (r?.ok) load();
  };

  const load = useCallback(async () => {
    const r = await fetch(`/api/garden/${who}`);
    if (r.status === 403) return setDenied(true);
    if (r.ok) {
      setDenied(false);
      const d = await r.json();
      // pin the cooldown to the wall clock — a static seconds snapshot never counts down
      d.fish_ready_at = Date.now() + (d.fish_next_in_s ?? 0) * 1000;
      setData(d);
      if (d.pet_gift) onToast?.('your buddy dug up a bonus water drop 🎁💧');
      if (self && d.pet_watered > 0)
        onToast?.(`your buddy watered ${d.pet_watered} seed${d.pet_watered > 1 ? 's' : ''} while you were away 💧`);
    }
    const v = await fetch(`/api/garden/${who}/visits`);
    if (v.ok) setVisits((await v.json()).visits ?? []);
    const p = await fetch(`/api/pet/${who}`);
    const pm = p.ok ? await p.json() : null;
    if (pm) {
      // the outfit rides the public profile — merge it onto petMeta so the
      // garden buddy (and everyone downstream) renders dressed
      const prof = await fetch(`/api/profile/${who}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
      pm.equip = prof?.equip ?? prof?.user?.equip;
    }
    setPetMeta(pm);
  }, [who]);

  useEffect(() => {
    load();
  }, [load, version]);

  const post = async (url, body) => {
    if (busy) return null;
    setBusy(true);
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        onToast?.(d.error ?? 'hm, that did not take');
        return null;
      }
      return d;
    } finally {
      setBusy(false);
    }
  };

  // keep the selection pointing at a live plant across refreshes
  useEffect(() => {
    if (!selected || !data) return;
    const fresh = data.plants.find((p) => p.id === selected.id);
    if (!fresh) setSelected(null);
    else if (fresh !== selected) setSelected(fresh);
  }, [data]);

  const onPlanted = async (px, py) => {
    const d = await post('/api/garden/plant', { px, py });
    if (d) {
      setMode(null);
      setData((prev) => ({ ...prev, plants: [d.plant, ...prev.plants], seeds: d.seeds }));
      setSelected(d.plant);
    }
  };

  const onMovedTo = async (px, py) => {
    if (!selected) return setMode(null);
    const d = await post('/api/garden/move', { plant_id: selected.id, px, py });
    if (d) {
      setMode(null);
      setData((prev) => ({
        ...prev,
        plants: prev.plants.map((p) => (p.id === selected.id ? { ...p, px, py } : p)),
      }));
      setSelected({ ...selected, px, py });
    }
  };

  const waterSelected = async () => {
    if (!selected) return;
    const d = await post('/api/garden/water', { plant_id: selected.id });
    if (d) {
      setData((prev) => ({
        ...prev,
        water: d.water,
        plants: prev.plants.map((p) => (p.id === d.plant.id ? { ...p, ...d.plant } : p)),
      }));
      setSelected((s) => (s && s.id === d.plant.id ? { ...s, ...d.plant } : s));
      if (d.plant.kind === 'grass') onToast?.('it woke up as grass! 🌱');
    }
  };

  // cast into the pond: server rolls the fish, we throw the little party
  const [fishCard, setFishCard] = useState(null);
  const onFish = async () => {
    if (!self) return false;
    const waitS = Math.ceil(((data?.fish_ready_at ?? 0) - Date.now()) / 1000);
    if (waitS > 0) {
      const m = Math.ceil(waitS / 60);
      onToast?.(`the pond needs quiet — bites again in ${m > 60 ? `${Math.ceil(m / 60)}h` : `${m}m`}`);
      return false;
    }
    const r = await fetch('/api/garden/fish', { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      onToast?.(d.error ?? 'the line snapped');
      if (d.next_in_s) setData((prev) => ({ ...prev, fish_ready_at: Date.now() + d.next_in_s * 1000 }));
      return false;
    }
    setTimeout(() => setFishCard(d), 900); // let the splash land first
    setData((prev) => {
      const log = [...(prev.fish_log ?? [])];
      const hit = log.find((f) => f.kind === d.fish.kind);
      if (hit) hit.n += 1;
      else log.push({ kind: d.fish.kind, rarity: d.fish.rarity, n: 1 });
      return { ...prev, water: d.water, seeds: d.seeds, fish_ready_at: Date.now() + d.next_in_s * 1000, fish_log: log };
    });
    return true;
  };

  // pick the golden fruit: server rolls the accessory, we throw the reveal party
  const [fruitCard, setFruitCard] = useState(null);
  const onHarvest = async () => {
    if (!self) return false;
    const r = await fetch('/api/garden/harvest', { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      onToast?.(d.error ?? 'the fruit slipped away');
      if (d.next_in_s) setData((prev) => ({ ...prev, fruit_ready: false, fruit_next_in_s: d.next_in_s }));
      return false;
    }
    setData((prev) => ({ ...prev, fruit_ready: false, fruit_next_in_s: d.next_in_s }));
    setTimeout(() => setFruitCard(d.fruit), 500); // let the glow wink out first
    return true;
  };
  const wearFruit = async () => {
    if (!fruitCard) return;
    const next = { ...(petMeta?.equip ?? {}), [fruitCard.slot]: fruitCard.design };
    const r = await fetch('/api/prefs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ equip: next }),
    });
    if (r.ok) {
      setPetMeta((prev) => (prev ? { ...prev, equip: next } : prev));
      onToast?.(`${fruitCard.design.replace(/_/g, ' ')} equipped ✨`);
    } else onToast?.('the closet drawer jammed — dress up from the me tab instead');
    setFruitCard(null);
  };

  const mergeSelected = async () => {
    if (!selected) return;
    const same = data.plants.filter((p) => p.kind === selected.kind && p.id !== selected.id);
    if (same.length < 2) return onToast?.(`grow three ${selected.kind} to merge them — you have ${same.length + 1}`);
    const d = await post('/api/garden/merge', { ids: [selected.id, same[0].id, same[1].id] });
    if (d) {
      setData((prev) => ({
        ...prev,
        plants: [d.plant, ...prev.plants.filter((p) => ![selected.id, same[0].id, same[1].id].includes(p.id))],
      }));
      setSelected(d.plant);
      onToast?.(`three ${selected.kind} became a ${d.plant.kind}!`);
    }
  };

  const plantButton = () => {
    if (mode === 'plant') return setMode(null);
    if ((data?.seeds ?? 0) < 1)
      return onToast?.('seeds drop when your agent finishes a run — link one from the me tab');
    setSelected(null);
    setMode('plant');
  };

  // friend pats count once a day server-side; hearts are always free
  const onPat = async () => {
    if (self || patSent.current) return;
    patSent.current = true;
    const d = await post(`/api/pet/${who}/pat`, {});
    if (d) {
      setPetMeta((prev) => ({ ...prev, ...d }));
      onToast?.(`pat delivered — ${who}'s buddy is ${d.happiness}`);
    }
  };

  const onAct = async (act) => {
    if (act.type === 'water') {
      const d = await post('/api/garden/water', { plant_id: act.plant.id });
      if (d)
        setData((prev) => ({
          ...prev,
          water: d.water,
          plants: prev.plants.map((p) => (p.id === d.plant.id ? { ...p, ...d.plant } : p)),
        }));
    } else if (act.type === 'merge') {
      const third = data.plants.find(
        (p) => p.kind === act.target.kind && p.id !== act.target.id && p.id !== act.dragged.id
      );
      if (!third) return onToast?.(`grow three ${act.target.kind} to merge them`);
      const d = await post('/api/garden/merge', { ids: [act.target.id, act.dragged.id, third.id] });
      if (d)
        setData((prev) => ({
          ...prev,
          plants: [d.plant, ...prev.plants.filter((p) => ![act.target.id, act.dragged.id, third.id].includes(p.id))],
        }));
    } else if (act.type === 'move') {
      const d = await post('/api/garden/move', { plant_id: act.plant.id, px: act.px, py: act.py });
      if (d)
        setData((prev) => ({
          ...prev,
          plants: prev.plants.map((p) => (p.id === act.plant.id ? { ...p, px: act.px, py: act.py } : p)),
        }));
    }
  };

  // share card: garden + who you are + your agents — a postcard with a quiet ad
  const share = async () => {
    const src = wrapRef.current?.querySelector('canvas');
    if (!src || !data) return;
    const prof = await fetch(`/api/profile/${who}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);

    // compose at 3x directly — bitmaps upscale honestly, but pixel FONTS must
    // render at native size (7px Silkscreen upscaled is mush; 21px is crisp)
    const s = 3;
    const CW = 416 * s;
    const CH = 306 * s;
    const out = document.createElement('canvas');
    out.width = CW;
    out.height = CH;
    const ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#16151f';
    ctx.fillRect(0, 0, CW, CH);

    // header: buddy + name + live agent dots
    const seed = petSeedFor(who);
    const sprite = spriteFor(seed, { stage: petMeta?.stage ?? 'adult', wins: petMeta?.wins ?? 0 });
    paintSprite(ctx, sprite, 8 * s, 4 * s, 2 * s, {});
    ctx.textBaseline = 'top';
    ctx.font = `${10 * s}px Silkscreen, monospace`;
    ctx.fillStyle = '#f2ede2';
    ctx.fillText(`@${who}`, 40 * s, 10 * s);
    ctx.font = `${7 * s}px Silkscreen, monospace`;
    let hx = 40 * s;
    const DOTC = { working: '#35d07f', waiting_input: '#f5c542', online: '#7fd0b0', idle: '#5a5f74' };
    const kindsLive = prof?.status?.kinds ?? [];
    for (const a of (prof?.agents ?? []).slice(0, 3)) {
      const st = kindsLive.find((k) => k.kind === a.kind)?.state ?? 'idle';
      ctx.fillStyle = DOTC[st] ?? DOTC.idle;
      ctx.fillRect(hx, 26 * s, 5 * s, 5 * s);
      ctx.fillStyle = '#9aa0b5';
      const label = `${agentLabel(a.kind)}${a.hours ? ` ${a.hours}h` : ''}${a.ships ? ` ⛵${a.ships}` : ''}`;
      ctx.fillText(label, hx + 8 * s, 25 * s);
      hx += 8 * s + ctx.measureText(label).width + 10 * s;
    }
    if (!(prof?.agents ?? []).length) {
      ctx.fillStyle = '#9aa0b5';
      ctx.fillText('vibe coder in residence', 40 * s, 25 * s);
    }

    // the garden itself — the postcard keeps the classic dims; a fullscreen canvas
    // donates its center crop at the W:H aspect instead of a squashed panorama
    const cropW = Math.min(src.width, Math.round((src.height * W) / H));
    const cropH = Math.min(src.height, Math.round((cropW * H) / W));
    ctx.drawImage(src, (src.width - cropW) >> 1, (src.height - cropH) >> 1, cropW, cropH, 8 * s, 40 * s, W * s, H * s);
    // side column: garden stats
    ctx.fillStyle = '#232130';
    ctx.fillRect(288 * s, 40 * s, 120 * s, H * s);
    const plants = data.plants ?? [];
    const counts = { grass: 0, flower: 0, sapling: 0, tree: 0, seed: 0 };
    for (const p of plants) counts[p.kind] = (counts[p.kind] ?? 0) + 1;
    ctx.fillStyle = '#f2ede2';
    ctx.font = `${8 * s}px Silkscreen, monospace`;
    ctx.fillText('this garden', 296 * s, 50 * s);
    ctx.font = `${7 * s}px Silkscreen, monospace`;
    ctx.fillStyle = '#9aa0b5';
    const amb = ambientNow(); // the postcard captures the ambient moment on the canvas
    const lines = [
      `${WEATHER_LABEL[amb.weather]} ${amb.stage}`,
      '',
      `seeds in pouch ${data.seeds ?? 0}`,
      `sprouts ${counts.seed}`,
      `grass ${counts.grass}`,
      `flowers ${counts.flower}`,
      `saplings ${counts.sapling}`,
      counts.tree ? 'THE tree stands' : 'no tree yet',
      '',
      'every plant grown by',
      'real agent runs',
    ];
    lines.forEach((l, i) => l && ctx.fillText(l, 296 * s, (66 + i * 13) * s));

    // footer: brand + pitch + add-me
    ctx.fillStyle = '#3b3654';
    ctx.fillRect(0, 254 * s, CW, s);
    const g = brandGrid();
    for (let y = 0; y < 16; y++)
      for (let x = 0; x < 16; x++)
        if (g[y][x]) {
          ctx.fillStyle = g[y][x];
          ctx.fillRect(8 * s + x * 2 * s, 260 * s + y * 2 * s, 2 * s, 2 * s);
        }
    ctx.fillStyle = '#f2ede2';
    ctx.font = `${10 * s}px Silkscreen, monospace`;
    ctx.fillText('vibebuddy.io', 48 * s, 264 * s);
    ctx.fillStyle = '#9aa0b5';
    ctx.font = `${7 * s}px Silkscreen, monospace`;
    ctx.fillText('the cosy hangout while your agent works', 48 * s, 280 * s);
    ctx.fillStyle = '#f5c542';
    ctx.font = `${8 * s}px Silkscreen, monospace`;
    const addme = `add me: @${who}`;
    ctx.fillText(addme, CW - 10 * s - ctx.measureText(addme).width, 268 * s);

    const text = `my pixel garden this ${amb.stage} 🌱 one seed per shipped agent run — add me on vibebuddy.io: @${who}`;
    out.toBlob((blob) => {
      if (!blob) return;
      // image straight onto the clipboard so any composer is one paste away
      try {
        navigator.clipboard?.write?.([new ClipboardItem({ 'image/png': blob })]).catch(() => {});
      } catch {}
      setShareCard({ url: URL.createObjectURL(blob), blob, text });
    });
  };

  // the desktop webview has no download UI and window.open goes nowhere —
  // both roads run through the shell there (Downloads dir / system browser)
  const desktopInvoke = window.__TAURI__?.core?.invoke;
  const shareOpen = (url) => {
    if (desktopInvoke) desktopInvoke('open_url', { url }).catch(() => {});
    else window.open(url, '_blank');
  };
  const shareSave = async () => {
    if (!shareCard) return;
    if (desktopInvoke) {
      try {
        const buf = new Uint8Array(await shareCard.blob.arrayBuffer());
        let bin = '';
        for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
        const path = await desktopInvoke('save_image', {
          name: `vibebuddy-${who}.png`,
          dataBase64: btoa(bin),
        });
        onToast?.(`saved to ${path} 💾`);
      } catch {
        onToast?.('save failed — copy still works');
      }
      return;
    }
    const a = document.createElement('a');
    a.href = shareCard.url;
    a.download = `vibebuddy-${who}.png`;
    a.click();
  };
  const shareCopy = async () => {
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': shareCard.blob })]);
      onToast?.('card copied — paste it anywhere 📋');
    } catch {
      onToast?.('clipboard said no — use save instead');
    }
  };
  // instagram has no web share intent — guide it: card onto the clipboard first,
  // then instagram itself, so the post is one paste away
  const shareInstagram = async () => {
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': shareCard.blob })]);
      onToast?.('postcard copied — paste it into your instagram post ✨');
    } catch {
      await shareSave(); // clipboard said no — hand over the file (it toasts the path itself)
    }
    shareOpen('https://www.instagram.com/');
  };
  const shareNative = async () => {
    try {
      const file = new File([shareCard.blob], `vibebuddy-${who}.png`, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: shareCard.text });
      } else {
        onToast?.('system share not available here — save or copy works everywhere');
      }
    } catch {}
  };

  if (denied) return html`<div class="dim">this garden keeps to itself.</div>`;
  if (!data) return html`<div class="dim">…</div>`;

  const w = data.water;
  const nextMin = w?.next_in_s != null ? Math.ceil(w.next_in_s / 60) : null;
  const sameKind = selected ? data.plants.filter((p) => p.kind === selected.kind).length : 0;
  // only tool-state lines live here now — everything else moved to the guide
  const hintText = !self
    ? `${who}'s buddy lives here — pat it gently`
    : mode === 'plant'
      ? 'tap a free tile to plant the seed'
      : mode === 'move'
        ? `tap a free tile to move the ${selected?.kind ?? 'plant'}`
        : selected
          ? selected.kind === 'seed'
            ? 'water it ×3 and it wakes up as grass'
            : selected.kind === 'tree'
              ? data.fruit_ready
                ? 'the golden fruit is ripe — tap its glow to harvest'
                : 'the big tree — one per gardener, forever'
              : `three ${selected.kind} merge into something bigger`
          : '';

  // the fullscreen wrapper centers the upscaled canvas on the app background;
  // inline styles only — the normal (non-fs) layout keeps the stylesheet's rules
  const fsWrapStyle = fs ? 'position:relative;display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:#16151f' : '';
  const hudRowStyle = 'display:flex;align-items:center;gap:6px;flex-wrap:wrap';
  // fullscreen HUD: floating pixel-framed clusters (the .pxframe chrome) that nap
  // after ~3s of pointer stillness and wake on any move
  const cluster = (pos) =>
    `position:absolute;${pos};${hudRowStyle};z-index:5;padding:6px 8px;background:var(--surface);transition:opacity .3s;opacity:${hud ? 1 : 0};pointer-events:${hud ? 'auto' : 'none'}`;
  // the selection action bar, shared verbatim between the windowed bar and the fs cluster
  const actionBits =
    self &&
    selected &&
    html`<span class="gchip">${selected.kind}${selected.kind === 'seed' ? html` · 💧<b>${selected.water_count}</b>/3` : ''}</span>
      ${selected.kind === 'seed' &&
      html`<button class="mini primary" onClick=${waterSelected}>💧 water</button>`}
      ${['grass', 'flower', 'sapling'].includes(selected.kind) &&
      html`<button class="mini primary" onClick=${mergeSelected}>✿ merge ×3 <span class="dim">(${sameKind})</span></button>`}
      <button class="mini ${mode === 'move' ? 'primary' : ''}"
        onClick=${() => setMode(mode === 'move' ? null : 'move')}>↔ move</button>
      <button class="mini" onClick=${() => { setSelected(null); setMode(null); }}>✕</button>`;

  return html`<div class="gardenpanel" ref=${wrapRef} style=${fsWrapStyle}>
    ${!fs &&
    html`<div class="gardenbar">
      <span style=${hudRowStyle}>
        ${self &&
        html`<span class="gchip">🌰 <b>${data.seeds}</b>/${data.seed_cap ?? 12}</span>
          <span class="gchip">💧 <b>${w.charges}</b>/${w.cap}${w.bonus ? html` <span title="bad-weather bonus drop">+1☔</span>` : ''}</span>
          ${nextMin != null && html`<span class="gchip dim">next 💧 ${nextMin > 60 ? `${Math.ceil(nextMin / 60)}h` : `${nextMin}m`}</span>`}`}
      </span>
      <span style="${hudRowStyle};margin-left:auto">
        ${self &&
        html`<button class="mini ${mode === 'plant' ? 'primary' : ''} ${(data.seeds ?? 0) < 1 ? 'dimbtn' : ''}"
            onClick=${plantButton}>${mode === 'plant' ? 'tap a tile…' : '🌱 plant'}</button>
          <button class="mini" onClick=${share}>📸 share</button>
          <button class="mini" onClick=${() => setGuide(true)}>? guide</button>`}
        ${!self &&
        html`<button class="mini" title="free — waters their ripest seed, once a day"
            onClick=${() => visitAct('water', `💧 watered ${who}'s garden`)}>💧 water</button>
          <button class="mini" title="leaves a butterfly for a day, once a day"
            onClick=${() => visitAct('butterfly', `🦋 a butterfly settles in ${who}'s garden`)}>🦋</button>
          <button class="mini" title="pat their buddy, once a day"
            onClick=${() => visitAct('pat', `🫶 patted ${who}'s buddy`)}>🫶</button>`}
        <button class="mini" title="fullscreen" onClick=${enterFs}>⛶</button>
        <button class="mini" title="reset the camera" onClick=${() => camRef.current?.reset()}>⟲ view</button>
      </span>
    </div>`}
    <${GardenCanvas} data=${data} visits=${visits} self=${self} onAct=${onAct}
      onToast=${onToast} mode=${mode} onPlanted=${onPlanted} onMovedTo=${onMovedTo}
      onSelect=${(p) => { setSelected(p); if (!p) setMode((m) => (m === 'move' ? null : m)); }}
      selected=${selected} petMeta=${petMeta} onPat=${onPat} onFish=${self ? onFish : null}
      onHarvest=${self ? onHarvest : null} camRef=${camRef} fs=${fs} />
    ${fs &&
    html`<div class="pxframe" style=${cluster('top:12px;left:12px')}>
        ${self
          ? html`<span class="gchip">🌰 <b>${data.seeds}</b>/${data.seed_cap ?? 12}</span>
              <span class="gchip">💧 <b>${w.charges}</b>/${w.cap}${w.bonus ? html` <span title="bad-weather bonus drop">+1☔</span>` : ''}</span>
              ${nextMin != null && html`<span class="gchip dim">next 💧 ${nextMin > 60 ? `${Math.ceil(nextMin / 60)}h` : `${nextMin}m`}</span>`}`
          : html`<span class="gchip">@${who}'s garden</span>
              <button class="mini" onClick=${() => visitAct('water', `💧 watered ${who}'s garden`)}>💧</button>
              <button class="mini" onClick=${() => visitAct('butterfly', `🦋 butterfly left`)}>🦋</button>
              <button class="mini" onClick=${() => visitAct('pat', `🫶 patted ${who}'s buddy`)}>🫶</button>`}
      </div>
      ${self &&
      html`<div class="pxframe" style=${cluster('top:12px;right:12px')}>
        <button class="mini ${mode === 'plant' ? 'primary' : ''} ${(data.seeds ?? 0) < 1 ? 'dimbtn' : ''}"
          onClick=${plantButton}>${mode === 'plant' ? 'tap a tile…' : '🌱 plant'}</button>
        <button class="mini" onClick=${share}>📸 share</button>
      </div>`}
      ${actionBits && html`<div class="pxframe" style=${cluster('bottom:56px;left:50%;transform:translateX(-50%)')}>${actionBits}</div>`}
      ${!actionBits && hintText && html`<div class="pxframe" style=${cluster('bottom:12px;left:12px')}><span class="gchip dim">${hintText}</span></div>`}
      <div class="pxframe" style=${cluster('bottom:12px;right:12px')}>
        <button class="mini" onClick=${() => setGuide(true)}>? guide</button>
        <button class="mini" title="reset the camera" onClick=${() => camRef.current?.reset()}>⟲ view</button>
        <button class="mini" title="exit fullscreen" onClick=${() => document.exitFullscreen?.()}>⛶ exit</button>
      </div>
      ${fsHint && html`<div class="gchip" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:6">press ESC to leave</div>`}`}
    ${!fs && actionBits && html`<div class="gardenbar actions">${actionBits}</div>`}
    ${!fs && hintText && html`<div class="gardenhint">${hintText}</div>`}
    ${!fs && self && (data.fish_log ?? []).length > 0 &&
    html`<div class="pondlog clickable" title="open the aquarium" onClick=${() => setAquarium(true)}>
      <div class="dim" style="margin:4px 0 2px">
        pond log · ${data.fish_log.reduce((a, f) => a + f.n, 0)} caught
        <span class="mini-link">🐟 aquarium ▸</span>
      </div>
      <div class="pillrow">
        ${data.fish_log.map(
          (f) => html`<span class="gchip fish-${f.rarity}" title=${f.rarity}>
            <${FishCanvas} kind=${f.kind} rarity=${f.rarity} cell=${2} /> ${f.kind} ×${f.n}
          </span>`
        )}
      </div>
    </div>`}
    ${aquarium &&
    html`<${Closet} me=${me} onToast=${onToast} onClose=${() => setAquarium(false)} tab="aquarium" />`}
    ${fishCard &&
    html`<div class="overlay hatchoverlay" onClick=${(e) => e.target.classList.contains('overlay') && setFishCard(null)}>
      <div class="hatchcard">
        <div class="hatchburst">
          <span class="confetti c1"></span><span class="confetti c2"></span>
          <span class="confetti c3"></span><span class="confetti c4"></span>
          <${FishCanvas} kind=${fishCard.fish.kind} rarity=${fishCard.fish.rarity} cell=${7} />
        </div>
        <h1 style="font-size:18px;margin:0">${fishCard.fish.kind}!</h1>
        <p class="dim" style="margin:6px 0 10px">
          ${fishCard.fish.rarity}${fishCard.reward === 'water' ? ' · it coughed up a water drop 💧' : ''}
          ${fishCard.reward === 'seed' ? ' · a seed was tangled in the line 🌰' : ''}
          ${fishCard.reward === 'water+seed' ? ' · treasure! +1💧 +1🌰' : ''}
        </p>
        <button class="primary" onClick=${() => setFishCard(null)}>release it home</button>
      </div>
    </div>`}
    ${fruitCard &&
    html`<div class="overlay hatchoverlay" onClick=${(e) => e.target.classList.contains('overlay') && setFruitCard(null)}>
      <div class="hatchcard">
        <div class="hatchburst">
          <span class="confetti c1"></span><span class="confetti c2"></span>
          <span class="confetti c3"></span><span class="confetti c4"></span>
          <${OutfitPreview} username=${who} meta=${petMeta}
            equip=${{ ...(petMeta?.equip ?? {}), [fruitCard.slot]: fruitCard.design }} cell=${7} />
        </div>
        <h1 style="font-size:18px;margin:0">${fruitCard.design.replace(/_/g, ' ')}!</h1>
        <p style="margin:6px 0 10px;color:${ACC_TINT[fruitCard.rarity] ?? ACC_TINT.common}">
          the fruit held a ${fruitCard.rarity} ${fruitCard.slot}
        </p>
        <div class="pillrow" style="justify-content:center">
          <button class="primary" onClick=${wearFruit}>wear it now</button>
          <button onClick=${() => setFruitCard(null)}>into the closet</button>
        </div>
      </div>
    </div>`}
    ${shareCard &&
    html`<div class="overlay" onClick=${(e) => e.target.classList.contains('overlay') && setShareCard(null)}>
      <div class="zoomcard">
        <div class="gardenhead">
          <b>your garden postcard</b>
          <span class="closex clickable" onClick=${() => setShareCard(null)}>✕</span>
        </div>
        <img src=${shareCard.url} class="pixel shareimg" />
        <div class="gardenhint">the image is already on your clipboard — paste it straight into any post 📋</div>
        <div class="pillrow sharebtns" style="margin-top:8px">
          <button class="mini primary sharebig" title="save" onClick=${shareSave}>💾</button>
          <button class="mini sharebig" title="copy" onClick=${shareCopy}>📋</button>
          <button class="mini sharebig" title="post on X" onClick=${() =>
            shareOpen(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareCard.text)}&url=https%3A%2F%2Fvibebuddy.io`)}>𝕏</button>
          <button class="mini sharebig" title="share on instagram" onClick=${shareInstagram}>ig</button>
          <button class="mini sharebig" title="share on facebook" onClick=${() =>
            shareOpen('https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fvibebuddy.io')}>f</button>
          <button class="mini sharebig" title="system share" onClick=${shareNative}>📲</button>
        </div>
        <div class="dim" style="margin-top:6px;font-size:12px">
          ig copies the card and opens instagram — paste it into a new post, your add-me tag rides the image
        </div>
      </div>
    </div>`}
    ${guide &&
    html`<div class="overlay" onClick=${(e) => e.target.classList.contains('overlay') && setGuide(false)}>
      <div class="zoomcard guidecard">
        <div class="gardenhead">
          <b>garden guide</b>
          <span class="closex clickable" onClick=${() => setGuide(false)}>✕</span>
        </div>
        <div class="guidesec"><b>🌰 seeds & water</b>
          <p>every finished agent turn drops a seed — the pouch grows with your buddy's level, and so does the
            watering can (refills 1💧 per 30 min). a seed drinks 3 drops and wakes up as grass.</p></div>
        <div class="guidesec"><b>🌱 growth path</b>
          <div style="display:flex;align-items:flex-end;gap:3px;flex-wrap:wrap;margin:6px 0 4px">
            <${MiniPlant} kind="seed" /><span class="dim" style="font-size:11px">→3💧→</span>
            <${MiniPlant} kind="grass" /><span class="dim" style="font-size:11px">→×3→</span>
            <${MiniPlant} kind="flower" /><span class="dim" style="font-size:11px">→×3→</span>
            <${MiniPlant} kind="sapling" /><span class="dim" style="font-size:11px">→×3→</span>
            <${MiniPlant} kind="tree" cell=${4} />
          </div>
          <p>a seed drinks 3💧 and wakes as grass; three of a kind merge into the next thing —
            grass → flower → sapling → <b>THE tree</b>, one per gardener, forever.</p></div>
        <div class="guidesec"><b>🍑 fruit & wardrobe</b>
          <p>the big tree ripens a <b style="color:#f5c542">golden fruit</b> every ~12h — tap its glow to harvest
            a random accessory for your buddy: a hat, shoes, or a necklace.</p>
          <div class="pillrow" style="margin:6px 0 4px">
            ${FRUIT_ODDS.map(
              ([r, pc]) => html`<span class="gchip" style="color:${ACC_TINT[r]};border-color:${ACC_TINT[r]}">
                <span style="display:inline-block;width:8px;height:8px;background:${ACC_TINT[r]}"></span> ${r} ${pc}
              </span>`
            )}
          </div>
          <p>duplicates keep — dress your buddy from the 👕 closet on the me tab, and the outfit rides along
            everywhere your buddy appears.</p></div>
        <div class="guidesec"><b>🎣 pond</b>
          <p>tap open water to cast — bites every 2h. common, uncommon, rare, legendary; rarer catches cough up
            water drops and even seeds.</p></div>
        <div class="guidesec"><b>🦋 butterflies</b>
          <p>friends leave them. each lives one day, then waters a seed on its way home.</p></div>
        <div class="guidesec"><b>🐾 buddy perks</b>
          <p>your buddy auto-waters your seeds — faster as it levels — and digs up a daily bonus drop when it
            feels loved.</p></div>
        <div class="guidesec"><b>🌗 sky</b>
          <p>your garden lives a whole day every 5 minutes — weather drifts through too. fireflies and the
            cottage window take the night shift.</p></div>
        <div class="guidesec"><b>🧭 camera</b>
          <p>drag empty ground to orbit and tilt · scroll to zoom · middle-drag for height · ⟲ resets the view
            · ⛶ fills the whole screen (esc leaves, the HUD naps until you move the pointer).</p></div>
      </div>
    </div>`}
    ${tut &&
    html`<div class="overlay hatchoverlay">
      <div class="hatchcard tutcard" style="max-width:330px">
        <h1 style="font-size:16px;margin:0">your garden 🌱</h1>
        <p class="dim" style="margin:8px 0 0">every finished agent turn drops a <b>seed</b>. plant it, water it ×3,
          then merge three of a kind: grass → flower → sapling → <b>one big tree</b>.</p>
        <p class="dim" style="margin:8px 0 0">the pond bites 🎣, butterflies visit 🦋, your buddy helps water —
          the <b>? guide</b> button above the garden has all the rules when you want them.</p>
        <button class="primary" style="margin-top:12px" onClick=${() => {
          localStorage.setItem('vb:garden-hello', '1');
          setTut(false);
        }}>got it</button>
      </div>
    </div>`}
  </div>`;
}

// friends wander in through a full overlay
export function GardenPage({ me, username, onClose, onToast }) {
  return html`<div class="overlay" onClick=${(e) => e.target.classList.contains('overlay') && onClose()}>
    <div class="profilecard gardenpage">
      <div class="gardenhead">
        <b>${username === me.username ? 'your garden' : `${username}'s garden`}</b>
        <span class="closex clickable" onClick=${onClose}>✕</span>
      </div>
      <${GardenPanel} me=${me} username=${username} self=${username === me.username} onToast=${onToast} />
    </div>
  </div>`;
}
